<template>
  <main v-if="apiReady" id="focus-page" class="panels">
    <b-container fluid>
      <DiscoverSettings 
        v-if="showNewBlockForm"        
        :settings="quickSettings"
        @initNewBlock="initNewBlock"
        @updateSettings="updateSettings"
        @changeLayout="changeLayout"
        @closeTopPanel="closeTopPanel"
      ></DiscoverSettings>
      <b-row v-if="showMissingAlert" class="mt-4">
        <b-col cols="6" offset="3">
          <b-card v-if="missingData.length > 0" title="There is an issue rendering this page" sub-title="It seems model data missing below elements">
            <b-list-group>
              <b-list-group-item v-for="m in missingData" :key="m">{{ m }}</b-list-group-item>
            </b-list-group>
            <b-card-text class="mt-4">
              <h2>Note: This means the presets will skip the visualizations which require any of above in its x or y axis.</h2>
            </b-card-text >
            <b-btn variant="outline-secondary" size="lg" class="mr-2" @click="skipInit()">Skip - Recommended</b-btn>
            <b-btn variant="outline-secondary" size="lg" @click="continueInit()">Load Presets Anyway!</b-btn>
          </b-card>
          <b-card v-else title="Welcome to Discoverboard!" sub-title="You can skip the preset or let system generate a preset for discover to get started. You can always reset it back to stage using reset option">
            <b-btn variant="outline-secondary" size="lg" class="mr-2" @click="skipInit()">Skip, I will do it manually!</b-btn>
            <b-btn variant="outline-secondary" size="lg" @click="continueInit()">I understand, load Presets!</b-btn>
          </b-card>
        </b-col>
      </b-row>
      <b-row v-else>
        <b-col>
          <b-tabs v-model="tabIndex" @input="tabChanged()" class="discover-tabs" vertical>
            <b-tab lazy>
              <template #title><img src="/images/info.png" /></template>
              <b-row class="m-0">
                <b-col class="content-grid">
                  <div class="content-grid-header">
                    <h3 class="ml-2">About</h3>
                  </div>
                  <div v-if="isAuthenticatedUserHasAccess && !showAboutPreview" class="p-2">
                    <b-form-group id="input-group-title" label="Title" label-for="input-title">
                      <b-form-input
                        id="input-title"
                        v-model.trim="form.title"
                        v-validate="{ required: true, max: 100 }"
                        placeholder="Title"
                        data-vv-name="title"
                      ></b-form-input>
                    </b-form-group>
                    <b-form-group id="input-group-subtitle" label="Sub Title" label-for="input-subtitle">
                      <b-form-input
                        id="input-subtitle"
                        v-model.trim="form.subtitle"
                        v-validate="{ required: true, max: 100 }"
                        placeholder="Sub Title"
                        data-vv-name="subtitle"
                      ></b-form-input>
                    </b-form-group>
                    <b-form-group id="input-group-subtitle2" label="Sub Title (Optional)" label-for="input-subtitle2">
                      <b-form-input
                        id="input-subtitle2"
                        v-model.trim="form.subtitle2"
                        v-validate="{ required: true, max: 100 }"
                        placeholder="Sub Title (Optional)"
                        data-vv-name="subtitle2"
                      ></b-form-input>
                    </b-form-group>
                    <b-form-group
                      id="input-group-description"
                      label="Description"
                      label-for="input-description"
                    >
                      <b-form-textarea
                        id="input-description"
                        v-model.trim="form.description"
                        v-validate="{ max: 5000 }"
                        placeholder="Description"
                        rows="15"
                        data-vv-name="description"
                      ></b-form-textarea>
                    </b-form-group>
                    <div class="text-right form-footer">
                      <b-btn
                        variant="outline-secondary"
                        size="sm"
                        @click="toggleAboutPreview"
                        class="mr-2"
                      >
                        <i class="fa fa-eye"></i> Preview
                      </b-btn>
                      <b-btn
                        variant="primary"
                        size="sm"
                        @click="saveAbout"
                      >
                        <i class="fa fa-save"></i> Save
                      </b-btn>
                    </div>
                  </div>
                  <div v-if="!isAuthenticatedUserHasAccess || showAboutPreview" class="p-2">
                    <h2>{{ form.title }}</h2>
                    <h3>{{ form.subtitle }}</h3>
                    <h4>{{ form.subtitle2 }}</h4>
                    <p>{{ form.description }}</p>
                    <div class="text-right form-footer">
                      <b-btn
                        variant="outline-secondary"
                        size="sm"
                        @click="toggleAboutPreview"
                        class="mr-2"
                      >
                        <i class="fa fa-pencil"></i> Edit
                      </b-btn>
                      <b-btn
                        variant="primary"
                        size="sm"
                        @click="saveAbout"
                      >
                        <i class="fa fa-save"></i> Save
                      </b-btn>
                    </div>
                  </div>
                </b-col>
              </b-row>
            </b-tab>
            <b-tab active lazy>
              <template #title><img src="/images/bubbles.png" /></template>
              <b-row class="m-0">
                <b-col class="content-grid">
                  <div class="content-grid-header">
                    <h3 class="ml-2">FieldsMap</h3>
                    <div class="tabs-major mr-auto ml-4">
                      <b-button-group>
                        <b-btn :class="{ 'btn-primary' : !appliedCatId, 'btn-outline-secondary' : appliedCatId }" @click="applyCat(null)">Default</b-btn>
                        <b-btn v-for="(cat) in discoverCats" :key="cat._id" :class="{ 'btn-outline-secondary' : appliedCatId!==cat._id, 'btn-primary' : appliedCatId===cat._id }" size="md" @click="applyCat(cat._id)">{{ cat.name }}</b-btn>
                      </b-button-group>
                    </div>
                    
                    <div class="actions">
                      <b-button :disabled="blockSelected !== null" variant="outline-secondary" @click="resetTab()"><i class="fas fa-broom"></i> Clear Selections</b-button>
                      <b-dropdown
                        variant="outline-secondary"
                        toggle-class="text-decoration-none"
                        right
                        no-caret
                        :disabled="blockSelected !== null"
                        class="ml-1"
                      >
                        <template slot="button-content"
                          ><i class="fa fa-ellipsis-v"
                        /></template>
                        <b-dropdown-item @click="resetTab()">
                          Clear Selections
                        </b-dropdown-item>
                        <b-dropdown-item v-if="isAuthenticatedUserHasAccess" @click="resetDiscover()">
                          Load Default Preset
                        </b-dropdown-item>
                        <!--<b-dropdown-item @click="newBlockForm()">
                          {{ showNewBlockForm? "Cancel":"Add New" }}
                        </b-dropdown-item>-->
                      </b-dropdown>
                    </div>
                  </div>
                  
                  <DocUniverseView
                    v-if="!showMissingAlert && docsDataState"
                    :show-controls="true"
                    :show-title="true"
                  ></DocUniverseView>
                  
                </b-col>
              </b-row>
            </b-tab>
            <b-tab lazy>
              <template #title><img src="/images/crosshair.png" /></template>
              <b-row class="m-0">
                <b-col class="vfilters " :class="filterBarVisible ? 'visible' : 'hidden'">
                  <b-row class="d-flex justify-content-between align-items-center">
                    <b-col v-if="filterBarVisible"><h3><i class="fas fa-filter"></i> Filters</h3></b-col>
                    <b-col v-else><h3><i class="fas fa-filter"></i></h3></b-col>
                    <b-col cols="2" class="text-center">
                      <b-button size="sm" variant="outline-transparent" @click="toggleFilterBar()" class="pr-0 pl-1"><i class="fas" :class="filterBarVisible ? 'fa-angle-double-left' : 'fa-angle-double-right'"></i></b-button>
                    </b-col>
                  </b-row>
                  <b-row>
                    <b-col>
                        <div v-if="filterBarVisible" class="treeview h80max">
                          <div class="levels level_1" v-for="(cat) in discoverCats" :key="cat._id">
                            <template v-if="checkChildrenVisibility(cat.children) && !discoverCategories.includes(cat._id) && !cat.name.includes('_del')">
                              <div class="level d-flex"
                                :id="cat._id"
                                :name="cat.name"
                              >
                                <i class="icon" :class="{'far fa-check-square': tree.selected[tabIndex].includes(cat._id), 'far fa-square': !tree.selected[tabIndex].includes(cat._id)}" @click="checkFilter(cat, null)" />
                                <span>{{ cat.name }}</span>
                                <i v-if="treeOpened[0].indexOf(cat._id) > -1" class="icon ml-auto cursor-pointer far fa-minus-square" @click="toggleCat(cat._id, 0)" />
                                <i v-else class="icon ml-auto cursor-pointer far fa-plus-square" @click="toggleCat(cat._id, 0)" />
                              </div>
                              <template v-if="treeOpened[0].includes(cat._id)">
                                <div class="level_2" v-for="(sub) in cat.children" :key="sub._id">
                                  <template v-if="checkChildrenVisibility(sub.children) && !discoverCategories.includes(sub._id) && !sub.name.includes('_del')">
                                    <div class="level d-flex"
                                      :id="sub._id"
                                      :name="sub.name"
                                    >
                                      <i class="icon" :class="{'far fa-check-square': tree.selected[tabIndex].includes(sub._id), 'far fa-square': !tree.selected[tabIndex].includes(sub._id)}" @click="checkFilter(sub, null)" />
                                      <span>{{ sub.name }}</span>
                                      <i v-if="treeOpened[0].indexOf(sub._id) > -1" class="icon ml-auto cursor-pointer far fa-minus-square" @click="toggleCat(sub._id, 0)" />
                                      <i v-else class="icon ml-auto cursor-pointer far fa-plus-square" @click="toggleCat(sub._id, 0)" />
                                    </div>
                                    <template v-if="treeOpened[0].includes(sub._id)">
                                      <div class="level_3" v-for="(filt) in sub.children" :key="filt._id">
                                        <template v-if="!discoverCategories.includes(filt._id) && !filt._id.includes('invalid_filter_') && !filt.name.includes('_del')">
                                          <div class="level d-flex"
                                            :id="filt._id"
                                            :name="filt.name"
                                            @click="checkFilter(filt._id, null)"
                                          ><i class="icon" :class="{'far fa-check-square': tree.selected[tabIndex].includes(filt._id), 'far fa-square': !tree.selected[tabIndex].includes(filt._id)}" /><span>{{ filt.name }}</span></div>
                                        </template>
                                      </div>
                                    </template>
                                  </template>
                                </div>
                              </template>
                            </template>
                          </div>
                        </div>
                        <!-- Place this where you want the vertical text -->
                        <div v-else class="vertical-text" @click="toggleFilterBar()">Open to view filters</div>
                        <!--<b-button variant="outline-dark" @click="resetFilters()">Reset</b-button>-->
                        <b-button v-if="filterBarVisible" variant="primary" class="fullWidthBt mt-4 ml-1" @click="applyFilters()">Apply</b-button>               
                    </b-col>
                  </b-row>
                </b-col>
                <b-col class="content-grid">
                  <div class="content-grid-header">
                    <h3 class="ml-2">Focus</h3>
                    <!--<div class="tabs-major mr-auto ml-4">
                      <b-button-group>
                        <b-btn :class="{ 'btn-primary' : !appliedCatId, 'btn-outline-secondary' : appliedCatId }" @click="applyCat(null)">Default</b-btn>
                        <b-btn v-for="(cat) in discoverCats" :key="cat._id" :class="{ 'btn-outline-secondary' : appliedCatId!==cat._id, 'btn-primary' : appliedCatId===cat._id }" size="md" @click="applyCat(cat._id)">{{ cat.name }}</b-btn>
                      </b-button-group>
                    </div>-->
                    <div class="actions ml-auto mr-1">
                      
                    </div>
                    <div class="actions">
                      <b-button v-if="isAuthenticatedUserHasAccess" :disabled="blockSelected !== null" variant="outline-secondary" @click="newBlockForm()" class="ml-1"><i class="fa fa-plus"></i> {{ showNewBlockForm? "Cancel":"Add New" }}</b-button>
                      <b-button :disabled="blockSelected !== null" variant="outline-secondary" @click="resetTab()" class="ml-1 mr-1"><i class="fas fa-broom"></i> Clear Selections</b-button>
                      <!--<b-button v-if="isAuthenticatedUserHasAccess" :disabled="blockSelected !== null" variant="outline-secondary" @click="resetDiscover()" class="ml-1"><i class="fas fa-eraser"></i> Load Default Preset</b-button>-->
                      <b-dropdown right variant="outline-secondary" toggle-class="text-decoration-none">
                        <template #button-content>
                          <span v-if="recencySelection === 'default'">
                            <i class="fas fa-sliders-h"></i>
                          </span>
                          <span v-else>
                            <i v-if="recencySelection === 'established'" class="fa fa-check-circle mr-1"></i>
                            <i v-if="recencySelection === 'trending'" class="fa fa-bolt mr-1"></i>
                            <i v-if="recencySelection === 'declining'" class="fa fa-arrow-down mr-1"></i>
                            {{
                              recencySelection === 'established' ? 'Established' :
                              recencySelection === 'trending' ? 'Trending' :
                              recencySelection === 'declining' ? 'Declining' :
                              ''
                            }}
                          </span>
                        </template>
                        <b-dropdown-item @click="showDefault()" :active="recencySelection === 'default'">
                          <i class="fas fa-sliders-h mr-1"></i> Default
                        </b-dropdown-item>
                        <b-dropdown-item @click="showEstablished()" :active="recencySelection === 'established'">
                          <i class="fa fa-check-circle mr-1"></i> Established
                        </b-dropdown-item>
                        <b-dropdown-item @click="showTrending()" :active="recencySelection === 'trending'">
                          <i class="fa fa-bolt mr-1"></i> Trending
                        </b-dropdown-item>
                        <b-dropdown-item @click="showDeclining()" :active="recencySelection === 'declining'">
                          <i class="fa fa-arrow-down mr-1"></i> Declining
                        </b-dropdown-item>
                      </b-dropdown>
                      <b-dropdown
                        variant="outline-secondary"
                        toggle-class="text-decoration-none"
                        right
                        no-caret
                        :disabled="blockSelected !== null"
                        class="ml-1"
                      >
                        <template slot="button-content"
                          ><i class="fa fa-ellipsis-v"
                        /></template>
                        <b-dropdown-item @click="resetTab()">
                          Clear Selections
                        </b-dropdown-item>
                        <b-dropdown-item v-if="isAuthenticatedUserHasAccess" @click="resetDiscover()">
                          Load Default Preset
                        </b-dropdown-item>
                        <b-dropdown-item v-if="isAuthenticatedUserHasAccess" @click="newBlockForm()">
                          {{ showNewBlockForm? "Cancel":"Add New" }}
                        </b-dropdown-item>
                      </b-dropdown>
                      <!--<b-button :disabled="blockSelected !== null" variant="danger" @click="resetDiscover()">Reset</b-button>
                      <b-button :disabled="blockSelected !== null" variant="warning" @click="newBlockForm()">{{ showNewBlockForm? "Close Editor":"Open Editor" }}</b-button>-->
                    </div>
                  </div>
                  <grid-layout
                    v-if="layout[tabIndex].length > 0"
                    :layout.sync="layout[tabIndex]"
                    :col-num="6"
                    :row-height="28"
                    :is-draggable="true"
                    :is-resizable="true"
                    :is-mirrored="false"
                    :vertical-compact="true"
                    :margin="[10, 10]"
                    :use-css-transforms="true"
                    @layout-ready="layoutReadyEvent"
                  >
                    <grid-item
                      v-for="item in layout[tabIndex]"
                      :key="item.i"
                      :class="{ maximized: blockMaximized.i === item.i }"
                      :x="item.x"
                      :y="item.y"
                      :w="item.w"
                      :h="item.h"
                      :i="item.i"
                      drag-allow-from=".vue-grid-item-title"
                      @resized="resizedEvent(item)"
                    >
                      <div
                        class="vue-grid-item-head"
                        :class="{ active: item.i === blockSelected }"
                      >
                        <div class="d-flex align-items-center">
                          <div class="vue-grid-item-title">
                            <i class="fa fa-arrows-alt"></i>
                          </div>
                          <div class="title">
                            <span
                              v-if="layoutReady && item.type !== 'placeholder'"
                              class="px-2 py-1"
                              >{{ item.name }}</span
                            >
                            <span
                              v-if="layoutReady && item.type === 'placeholder'"
                              class="px-2 py-1"
                              >Add New</span
                            >
                          </div>
                        </div>
                        <div class="vue-grid-item-head--actions">
                          <b-btn-group v-if="item.type !== 'placeholder'">
                            <b-dropdown v-if="blockSelected === null" size="sm" variant="outline-secondary">
                              <template slot="button-content">
                                <span class="export" />
                              </template>
                              <b-dropdown-item @click="downloadSvg(item.i)">
                                Export as SVG
                              </b-dropdown-item>
                              <b-dropdown-item @click="downloadPng(item.i)">
                                Export as PNG
                              </b-dropdown-item>
                              <!--<b-dropdown-item @click="downloadPpt">
                                Export as PPT
                              </b-dropdown-item>-->
                              <b-dropdown-item @click="downloadXls(item.i)">
                                Export as XLS
                              </b-dropdown-item>
                            </b-dropdown>
                            <b-btn
                              v-if="blockSelected !== item.i"
                              size="sm"
                              variant="outline-secondary"
                              @click="selectBlock(item.i)"
                              ><span class="preferences"></span>
                            </b-btn>
                            <b-btn
                              v-if="blockSelected === item.i"
                              size="sm"
                              variant="outline-secondary"
                              @click="selectBlock(item.i)"
                              ><span class="closeoptions"></span>
                            </b-btn>
                            <b-btn
                              v-if="
                                blockSelected === null && blockMaximized.i !== item.i
                              "
                              size="sm"
                              variant="outline-secondary"
                              @click="maximizeBlock(item)"
                              ><span class="fullscreen"></span>
                            </b-btn>
                            <b-btn
                              v-if="
                                blockSelected === null && blockMaximized.i === item.i
                              "
                              size="sm"
                              variant="outline-secondary"
                              @click="maximizeBlock(item)"
                              ><span class="normalscreen"></span>
                            </b-btn>
                            <b-btn
                              v-if="blockSelected === null && isAuthenticatedUserHasAccess"
                              size="sm"
                              variant="outline-secondary"
                              @click="removeBlock(item)"
                              ><span class="deleteblock"></span>
                            </b-btn>
                          </b-btn-group>
                        </div>
                      </div>
                      <div :ref="`anlview${item.i}`">
                      <SingleChartView
                        v-if="layoutReady && !(['documentstable','clustermap'].includes(item.type))"
                        :ref="`anl${item.i}`"
                        :chart-type="item.type"
                        :analyse="item.query"
                        :settings="item.settings"
                        :responsive="true"
                        :block="item.i"
                        :showControls="item.i === blockSelected"
                        @setDefault="setDefault"
                      ></SingleChartView>
                      </div>
                      <DimredPlotView
                        v-if="layoutReady && item.type === 'clustermap'"
                        :analyse="item.query"
                        :responsive="true"
                        >
                      </DimredPlotView>
                      <DocumentsTableFocus
                        v-if="layoutReady && item.type === 'documentstable'"
                        :analyse="item.query"
                        :block="item.i"
                        @viewDocument="showDocumentModal"
                      ></DocumentsTableFocus>
                      <b-row v-if="layoutReady && item.type === 'placeholder'">
                        <b-col cols="8" offset="2" class="mt-4 placeholder">
                          <b-form-select
                            v-model="layoutSelected"
                            :options="layoutTypes"
                          ></b-form-select>
                          <b-button class="mt-2" @click="addNewBlock"
                            ><i class="fa fa-plus"></i><span> Add New</span></b-button
                          >
                        </b-col>
                      </b-row>
                    </grid-item>
                  </grid-layout>
                </b-col>
              </b-row>
            </b-tab>
            <b-tab lazy>
              <template #title><img src="/images/smartphone.png" /></template>
              <b-row class="m-0">
                <b-col cols="2" class="vfilters">
                  <b-row class="d-flex">
                    <b-col class="card-tabs inner-tabs whitecard pt-0">
                      
                      <b-tabs v-model="correlationTab" card>
                        <b-tab title="X-Axis" class="px-0 pb-0 mb-0" active>
                          <div class="treeview h80">
                            <div class="levels level_1" v-for="(cat) in discoverCats" :key="cat._id">
                              <template v-if="checkChildrenVisibility(cat.children) && !discoverCategories.includes(cat._id) && !cat.name.includes('_del')">
                                <div class="level d-flex"
                                  :id="cat._id"
                                  :name="cat.name"
                                >
                                  <i class="icon" :class="{'far fa-check-square': checkTree(cat._id, 0), 'far fa-square': !checkTree(cat._id, 0)}" @click="checkFilter(cat, 0)" />
                                  <span>{{ cat.name }}</span>
                                  <i v-if="treeOpened[0].indexOf(cat._id) > -1" class="icon ml-auto cursor-pointer far fa-minus-square" @click="toggleCat(cat._id, 0)" />
                                  <i v-else class="icon ml-auto cursor-pointer far fa-plus-square" @click="toggleCat(cat._id, 0)" />
                                </div>
                                <template v-if="treeOpened[0].includes(cat._id)">
                                  <div class="level_2" v-for="(sub) in cat.children" :key="sub._id">
                                    <template v-if="checkChildrenVisibility(sub.children) && !discoverCategories.includes(sub._id) && !sub.name.includes('_del')">
                                      <div class="level d-flex"
                                        :id="sub._id"
                                        :name="sub.name"
                                      >
                                        <i class="icon" :class="{'far fa-check-square': checkTree(sub._id, 0), 'far fa-square': !checkTree(sub._id, 0)}" @click="checkFilter(sub, 0)" />
                                        <span>{{ sub.name }}</span>
                                        <i v-if="treeOpened[0].indexOf(sub._id) > -1" class="icon ml-auto cursor-pointer far fa-minus-square" @click="toggleCat(sub._id, 0)" />
                                        <i v-else class="icon ml-auto cursor-pointer far fa-plus-square" @click="toggleCat(sub._id, 0)" />
                                      </div>
                                      <template v-if="treeOpened[0].includes(sub._id)">
                                        <div class="level_3" v-for="(filt) in sub.children" :key="filt._id">
                                          <template v-if="!discoverCategories.includes(filt._id) && !filt._id.includes('invalid_filter_') && !filt.name.includes('_del')">
                                            <div class="level d-flex" 
                                              :id="filt._id"
                                              :name="filt.name"
                                              @click="checkFilter(filt._id, 0)"
                                            ><i class="icon" :class="{'far fa-check-square': checkTree(filt._id, 0), 'far fa-square': !checkTree(filt._id, 0)}" /><span>{{ filt.name }}</span></div>
                                          </template>
                                        </div>
                                      </template>
                                    </template>
                                  </div>
                                </template>
                              </template>
                            </div>
                          </div>
                        </b-tab>
                        <b-tab title="Y-Axis" class="px-0 pb-0 mb-0">
                          <div class="treeview h80">
                            <div class="levels level_1" v-for="(cat) in discoverCats" :key="cat._id">
                              <template v-if="checkChildrenVisibility(cat.children) && !discoverCategories.includes(cat._id) && !cat.name.includes('_del')">
                                <div class="level d-flex"
                                  :id="cat._id"
                                  :name="cat.name"
                                >
                                  <i class="icon" :class="{'far fa-check-square': checkTree(cat._id, 1), 'far fa-square': !checkTree(cat._id, 1)}" @click="checkFilter(cat, 1)" />
                                  <span>{{ cat.name }}</span>
                                  <i v-if="treeOpened[1].indexOf(cat._id) > -1" class="icon ml-auto cursor-pointer far fa-minus-square" @click="toggleCat(cat._id, 1)" />
                                  <i v-else class="icon ml-auto cursor-pointer far fa-plus-square" @click="toggleCat(cat._id, 1)" />
                                </div>
                                <template v-if="treeOpened[1].includes(cat._id)">
                                  <div class="level_2" v-for="(sub) in cat.children" :key="sub._id">
                                    <template v-if="checkChildrenVisibility(sub.children) && !discoverCategories.includes(sub._id) && !sub.name.includes('_del')">
                                      <div class="level d-flex"
                                        :id="sub._id"
                                        :name="sub.name"
                                      >
                                        <i class="icon" :class="{'far fa-check-square': checkTree(sub._id, 1), 'far fa-square': !checkTree(sub._id, 1)}" @click="checkFilter(sub, 1)" />
                                        <span>{{ sub.name }}</span>
                                        <i v-if="treeOpened[1].indexOf(sub._id) > -1" class="icon ml-auto cursor-pointer far fa-minus-square" @click="toggleCat(sub._id, 1)" />
                                        <i v-else class="icon ml-auto cursor-pointer far fa-plus-square" @click="toggleCat(sub._id, 1)" />
                                      </div>
                                      <template v-if="treeOpened[1].includes(sub._id)">
                                        <div class="level_3" v-for="(filt) in sub.children" :key="filt._id">
                                          <template v-if="!discoverCategories.includes(filt._id) && !filt._id.includes('invalid_filter_') && !filt.name.includes('_del')">
                                            <div class="level d-flex" 
                                              :id="filt._id"
                                              :name="filt.name"
                                              @click="checkFilter(filt._id, 1)"
                                            ><i class="icon" :class="{'far fa-check-square': checkTree(filt._id, 1), 'far fa-square': !checkTree(filt._id, 1)}" /><span>{{ filt.name }}</span></div>
                                          </template>
                                        </div>
                                      </template>
                                    </template>
                                  </div>
                                </template>
                              </template>
                            </div>
                          </div>
                        </b-tab>
                      </b-tabs>
                      
                    </b-col>
                  </b-row>
                  <b-row>
                    <b-col>
                      <!--<b-button variant="outline-dark" @click="resetFilters()">Reset</b-button>-->
                      <b-button variant="primary" class="fullWidthBt ml-1" @click="correlationsAxis(null, 'y')">Apply</b-button>
                    </b-col>
                  </b-row>
                </b-col>
                <b-col cols="10" class="content-grid">
                  <div class="content-grid-header">
                    <h3 class="ml-2"></h3>
                    <!--<div class="tabs-major mr-auto ml-4">
                      <b-button-group>
                        <b-btn :class="{ 'btn-primary' : !appliedCatId, 'btn-outline-secondary' : appliedCatId }" @click="applyCat(null)">Default</b-btn>
                        <b-btn v-for="(cat) in discoverCats" :key="cat._id" :class="{ 'btn-outline-secondary' : !correlationsCatIds.includes(cat._id), 'btn-primary' : correlationsCatIds.includes(cat._id) }" size="md" @click="correlationsAxis(cat._id, 'x')">{{ cat.name }}</b-btn>
                      </b-button-group>
                    </div>-->
                    <div class="actions ml-auto mr-1">
                      
                    </div>
                    <div>
                      <b-button :disabled="blockSelected !== null" variant="outline-secondary" @click="resetTab()" class="ml-1 mr-1"><i class="fas fa-broom"></i> Clear Selections</b-button>
                      <!--<b-button v-if="isAuthenticatedUserHasAccess" :disabled="blockSelected !== null" variant="outline-secondary" @click="resetDiscover()" class="ml-1"><i class="fas fa-eraser"></i> Load Default Preset</b-button>-->
                      <b-dropdown right variant="outline-secondary" toggle-class="text-decoration-none">
                        <template #button-content>
                          <span v-if="recencySelection === 'default'">
                            <i class="fas fa-sliders-h"></i>
                          </span>
                          <span v-else>
                            <i v-if="recencySelection === 'established'" class="fa fa-check-circle mr-1"></i>
                            <i v-if="recencySelection === 'trending'" class="fa fa-bolt mr-1"></i>
                            <i v-if="recencySelection === 'declining'" class="fa fa-arrow-down mr-1"></i>
                            {{
                              recencySelection === 'established' ? 'Established' :
                              recencySelection === 'trending' ? 'Trending' :
                              recencySelection === 'declining' ? 'Declining' :
                              ''
                            }}
                          </span>
                        </template>
                        <b-dropdown-item @click="showDefault()" :active="recencySelection === 'default'">
                          <i class="fas fa-sliders-h mr-1"></i> Default
                        </b-dropdown-item>
                        <b-dropdown-item @click="showEstablished()" :active="recencySelection === 'established'">
                          <i class="fa fa-check-circle mr-1"></i> Established
                        </b-dropdown-item>
                        <b-dropdown-item @click="showTrending()" :active="recencySelection === 'trending'">
                          <i class="fa fa-bolt mr-1"></i> Trending
                        </b-dropdown-item>
                        <b-dropdown-item @click="showDeclining()" :active="recencySelection === 'declining'">
                          <i class="fa fa-arrow-down mr-1"></i> Declining
                        </b-dropdown-item>
                      </b-dropdown>
                      <b-dropdown
                        variant="outline-secondary"
                        toggle-class="text-decoration-none"
                        right
                        no-caret
                        :disabled="blockSelected !== null"
                        class="ml-1"
                      >
                        <template slot="button-content"
                          ><i class="fa fa-ellipsis-v"
                        /></template>
                        <b-dropdown-item @click="toggleCr()">
                          Switch to {{ layout[tabIndex].length>0? layout[tabIndex][0].type === "heatmap"? "ClusterMap":"HeatMap" :"" }}
                        </b-dropdown-item>
                        <b-dropdown-item @click="resetTab()">
                          Clear Selections
                        </b-dropdown-item>
                        <b-dropdown-item v-if="isAuthenticatedUserHasAccess" @click="resetDiscover()">
                          Load Default Preset
                        </b-dropdown-item>
                        <!--<b-dropdown-item @click="newBlockForm()">
                          {{ showNewBlockForm? "Cancel":"Add New" }}
                        </b-dropdown-item>-->
                      </b-dropdown>
                      <!--<b-button :disabled="blockSelected !== null" variant="danger" @click="resetDiscover()">Reset</b-button>
                      <b-button :disabled="blockSelected !== null" variant="warning" @click="newBlockForm()">{{ showNewBlockForm? "Close Editor":"Open Editor" }}</b-button>-->
                    </div>
                  </div>
                  <grid-layout
                    :layout.sync="layout[tabIndex]"
                    :col-num="6"
                    :row-height="28"
                    :is-draggable="true"
                    :is-resizable="true"
                    :is-mirrored="false"
                    :vertical-compact="true"
                    :margin="[10, 10]"
                    :use-css-transforms="true"
                    @layout-ready="layoutReadyEvent"
                  >
                    <grid-item
                      v-for="item in layout[tabIndex]"
                      :key="item.i"
                      :class="{ maximized: blockMaximized.i === item.i }"
                      :x="item.x"
                      :y="item.y"
                      :w="item.w"
                      :h="item.h"
                      :i="item.i"
                      drag-allow-from=".vue-grid-item-title"
                      @resized="resizedEvent(item)"
                    >
                      <div
                        class="vue-grid-item-head"
                        :class="{ active: item.i === blockSelected }"
                      >
                        <div class="d-flex align-items-center">
                          <div class="vue-grid-item-title">
                            <i class="fa fa-arrows-alt"></i>
                          </div>
                          <div class="title">
                            <span
                              v-if="layoutReady && item.type !== 'placeholder'"
                              class="px-2 py-1"
                              >{{ item.name }}</span
                            >
                            <span
                              v-if="layoutReady && item.type === 'placeholder'"
                              class="px-2 py-1"
                              >Add New</span
                            >
                          </div>
                        </div>
                        <div class="vue-grid-item-head--actions">
                          <b-btn-group v-if="item.type !== 'placeholder'">
                            <b-dropdown v-if="blockSelected === null" size="sm" variant="outline-secondary">
                              <template slot="button-content">
                                <span class="export" />
                              </template>
                              <b-dropdown-item @click="downloadSvg(item.i)">
                                Export as SVG
                              </b-dropdown-item>
                              <b-dropdown-item @click="downloadPng(item.i)">
                                Export as PNG
                              </b-dropdown-item>
                              <!--<b-dropdown-item @click="downloadPpt">
                                Export as PPT
                              </b-dropdown-item>-->
                              <b-dropdown-item @click="downloadXls(item.i)">
                                Export as XLS
                              </b-dropdown-item>
                            </b-dropdown>
                            <b-btn
                              v-if="blockSelected !== item.i"
                              size="sm"
                              variant="outline-secondary"
                              @click="selectBlock(item.i)"
                              ><span class="preferences"></span>
                            </b-btn>
                            <b-btn
                              v-if="blockSelected === item.i"
                              size="sm"
                              variant="outline-secondary"
                              @click="selectBlock(item.i)"
                              ><span class="closeoptions"></span>
                            </b-btn>
                            <!--<b-btn
                              v-if="
                                blockSelected === null && blockMaximized.i !== item.i
                              "
                              size="sm"
                              variant="outline-secondary"
                              @click="maximizeBlock(item)"
                              ><span class="fullscreen"></span>
                            </b-btn>
                            <b-btn
                              v-if="
                                blockSelected === null && blockMaximized.i === item.i
                              "
                              size="sm"
                              variant="outline-secondary"
                              @click="maximizeBlock(item)"
                              ><span class="normalscreen"></span>
                            </b-btn>
                            <b-btn
                              v-if="blockSelected === null && isAuthenticatedUserHasAccess"
                              size="sm"
                              variant="outline-secondary"
                              @click="removeBlock(item)"
                              ><span class="deleteblock"></span>
                            </b-btn>-->
                          </b-btn-group>
                        </div>
                      </div>
                      <div :ref="`anlview${item.i}`">
                      <SingleChartView
                        v-if="layoutReady && !(['documentstable','clustermap'].includes(item.type))"
                        :ref="`anl${item.i}`"
                        :chart-type="item.type"
                        :analyse="item.query"
                        :settings="item.settings"
                        :responsive="true"
                        :block="item.i"
                        :showControls="item.i === blockSelected"
                        @setDefault="setDefault"
                      ></SingleChartView>
                      </div>
                      <DimredPlotView
                        v-if="layoutReady && item.type === 'clustermap'"
                        :analyse="item.query"
                        :settings="item.settings"
                        :responsive="true"
                        >
                      </DimredPlotView>
                      <DocumentsTableFocus
                        v-if="layoutReady && item.type === 'documentstable'"
                        :analyse="item.query"
                        :block="item.i"
                        @viewDocument="showDocumentModal"
                      ></DocumentsTableFocus>
                      <b-row v-if="layoutReady && item.type === 'placeholder'">
                        <b-col cols="8" offset="2" class="mt-4 placeholder">
                          <b-form-select
                            v-model="layoutSelected"
                            :options="layoutTypes"
                          ></b-form-select>
                          <b-button class="mt-2" @click="addNewBlock"
                            ><i class="fa fa-plus"></i><span> Add New</span></b-button
                          >
                        </b-col>
                      </b-row>
                    </grid-item>
                  </grid-layout> 
                </b-col>
              </b-row>
            </b-tab>
          </b-tabs>
        </b-col>
      </b-row>
      <div id="hoverDiv"></div>
    </b-container>
    <panelOptions
      v-if="showOptionsPanel"
      :element="activeElement"
    ></panelOptions>

    <PanelLeft
      :class="{ opened: showLeftSidebar }"
      @toggleLeftSidebar="toggleLeftSidebar"
    ></PanelLeft>

    <div
      class="container-fluid panel-bottom"
      :class="{
        opened: showDocumentsPanel,
        //leftopened: showLeftSidebar,
        //rightopened: showExploreSidebar,
        //leftrightopened: showLeftSidebar && showExploreSidebar,
        expanded: showDocumentsPanel && panelExpanded === 'expanded',
      }"
    >
      <b-row class="panel-head pt-2 pb-2">
        <b-col cols="7" class="d-flex align-items-center">
          <p class="text-overflow-ellipsis">
            <strong>{{ documentsCount }}</strong> documents |
            <span class="text-muted">
              <strong>filter:</strong> {{ filterFocused ? filterFocused.name : "none" }} | 
              <strong>selection:</strong>&nbsp;
            </span>          
          </p>
          <template v-if="tabIndex === 3 && activeElement && documentSelection.length">
            <b-badge
                class="text-overflow-ellipsis"
                variant="secondary">
            {{
              activeElement
                ? Array.isArray(activeElement)
                  ? activeElement[0].name
                  : activeElement.name
                : "none"
            }}
            </b-badge>
          </template>
          <b-badge
                v-for="el in selectedElements"
                :key="el.name"
                class="text-overflow-ellipsis"
                variant="secondary"
                >{{ el.name }}</b-badge
          >&nbsp;&nbsp;
          <b-badge
                v-for="el in elementTypes"
                :key="el.name"
                class="text-overflow-ellipsis"
                variant="secondary"
                >{{ el.name }}</b-badge
          >
          <b-btn
            v-if="selectedElements.length"
            variant="trasparent"
            class="mr-1 ml-2"
            @click="clearDocFilter"
          >
            <i class="fa fa-eraser" />
          </b-btn>
        </b-col>
        <b-col class="d-flex justify-content-end align-items-center">
          <b-btn
            v-if="showDocumentsPanel"
            variant="outline-secondary"
            class="mr-1"
            @click="resizeDocumentsPanel"
          >
            <i class="fa fa-expand" />
          </b-btn>
          <b-btn
            v-if="showDocumentsPanel"
            variant="outline-secondary"
            class="mr-1"
            @click="clearDocFilter"
          >
            <i class="fa fa-undo" />
          </b-btn>
          <xlsx-workbook
            v-if="exportReady"
            class="btn mr-1 btn-outline-secondary p-0 d-flex"
          >
            <xlsx-sheet :collection="exportData" :sheet-name="'sheet name'" />
            <xlsx-download>
              <b-btn variant="white"><i class="fa fa-download" /></b-btn>
            </xlsx-download>
          </xlsx-workbook>
          <b-button
            v-else
            variant="outline-secondary"
            class="mr-1"
            @click="exportDocs"
          >
            <b-spinner v-if="docsLoading" small type="grow"></b-spinner>
            {{ docsLoading ? "Downloading Docs" : "Export Docs" }}
          </b-button>
          <b-form-select
            v-model="selectedCollection"
            class="custom-select custom-select-collections mr-1 mb-0"
            :options="options"
            @change="getCollectionDocs"
          ></b-form-select>
          <b-btn variant="primary" @click="toggleDocumentsPanel">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="currentColor"
              class="bi bi-chevron-double-down"
              viewBox="0 0 16 16"
              v-if="showDocumentsPanel"
            >
              <path
                fill-rule="evenodd"
                d="M1.646 6.646a.5.5 0 0 1 .708 0L8 12.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"
              />
              <path
                fill-rule="evenodd"
                d="M1.646 2.646a.5.5 0 0 1 .708 0L8 8.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"
              />
            </svg>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="currentColor"
              class="bi bi-chevron-double-up"
              viewBox="0 0 16 16"
              v-else
            >
              <path
                fill-rule="evenodd"
                d="M7.646 2.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1-.708.708L8 3.707 2.354 9.354a.5.5 0 1 1-.708-.708l6-6z"
              />
              <path
                fill-rule="evenodd"
                d="M7.646 6.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1-.708.708L8 7.707l-5.646 5.647a.5.5 0 0 1-.708-.708l6-6z"
              />
            </svg>
            <span class="ml-2">Document panel</span>
          </b-btn>
        </b-col>
      </b-row>
      <b-row class="panel-body">
        <b-col><PanelBottom ref="docPanel"></PanelBottom></b-col>
      </b-row>
    </div>
    <chatbot @toggleDocumentsPanel="toggleDocumentsPanel" v-if="chatbotState" />
  </main>
</template>

<script>
import _ from "lodash";
import VueGridLayout from "vue-grid-layout";
import { XlsxDownload, XlsxSheet, XlsxWorkbook } from "vue-xlsx";
import { mapActions, mapGetters, mapState } from "vuex";
import { EventBus } from "@/eventBus";
import DataSettings from "@/helpers/DataSettings";
import DocumentsTableFocus from "../Shared/DocsTableFocus";
import InitApi from "../Shared/InitApi";
import PanelBottom from "../Shared/PanelBottom";
import PanelLeft from "../Shared/PanelLeft";
import PanelOptions from "../Shared/PanelOptions";
import SingleChartView from "../Visualizations/SingleChartView";
import DimredPlotView from "../Visualizations/DimredPlotView";
import DocUniverseView from "../Visualizations/DocUniverseView";
import TableView from "../Visualizations/TableView";
import DiscoverSettings from '../Shared/Settings/DiscoverSettings';
import {
  downloadPpt,
  downloadSvg,
  downloadSvg2Png,
  downloadXls
} from "../Visualizations/shared/utilities";
import { settingControls, defaultColorOptions, defaultMetricOptions, defaultSortOptions } from "../Visualizations/shared/controls";
import { getFiltId, prepFilter, parseSelectedElements } from "../../../helpers/PrepFilters";
import Chatbot from '../Chatbot/Chatbot.vue';

export default {
  name: "ExploreDiscover",
  components: {
    DocumentsTableFocus,
    SingleChartView,
    DimredPlotView,
    PanelLeft,
    PanelBottom,
    PanelOptions,
    DiscoverSettings,
    GridLayout: VueGridLayout.GridLayout,
    GridItem: VueGridLayout.GridItem,
    XlsxWorkbook,
    XlsxSheet,
    XlsxDownload,
    DocUniverseView,
    TableView,
    Chatbot
  },
  extends: InitApi,
  data() {
    return {
      discoverReady: false,
      years: [],
      settings: null,
      activeElement: null,
      elementTypes: [],
      selectedElements: [],
      selectedElementsObjects: [],
      showDocumentWebkey: null,
      showLeftSidebar: false,
      showTopPanel: false,
      showDocumentsPanel: false,
      showOptionsPanel: false,
      layoutReady: false,
      layoutTypes: [
        { value: null, text: "Please select an option" },
        { value: "worldmap", text: "World Map" },
        { value: "documentstable", text: "Documents Table" },
        { value: "onedheatmap", text: "1D Heat Map" },
        { value: "scatterplot", text: "Scatter Plot" },
        { value: "barchart", text: "Bar Chart" },
        { value: "columnchart", text: "Column Chart" },
        { value: "table", text: "Table View" },
      ],
      layoutSelected: null,
      layout: [],
      blockSelected: null,
      blockMaximized: { i: null, index: null, item: null, landmark: null },
      quickSettings: null,
      chartSettings: null,
      options: [{ value: null, text: "Collections" }],
      selectedCollection: null,
      exportReady: false,
      docsLoading: false,
      exportData: [],
      tree: {
        selected: [[],[],[],[[],[]]]
      },
      treeOpened: [[],[]],
      filterBarVisible: false,
      showNewBlockForm: false,
      tabIndex: 1,
      missingData: [],
      showMissingAlert: false,
      recencySelection: "default",
      appliedCatId: null,
      correlationsCatIds: [],
      showAboutPreview: false,
      form: {
        title: null,
        subtitle: null,
        subtitle2: null,
        description: null
      },
      correlationTab: 0
    };
  },
  computed: {
    ...mapState({
      apiId: (state) => state.api.apiId,
      apiReady: (state) => state.api.apiReady,
      //settings: (state) => state.setting.dataSettings,
      discoverCategories: (state) => state.setting.dataSettings.focus.lastQuery,
      documents: (state) => state.document.documents,
      documentsPerYear: (state) => state.document.documentsPerYear,
      documentsExport: (state) => state.document.documentsExport,
      documentsCount: (state) => state.document.documentsCount,
      documentSelection: (state) => state.document.documentSelection,
      filterFocused: (state) => state.filter.filterFocused,
      subsetFilter: (state) => state.filter.subsetFilter,
      panelExpanded: (state) => state.document.documentsPanelState,
      collections: (state) => state.collection.collections,
      filters: (state) => state.filter.filters,
      indices: (state) => state.ttc.indices,
      metas: (state) => state.ttc.metas,
      chatbotState: (state) => state.chatbot.chatbotState,
      docsDataState: (state) => state.layout.docsDataState
    }),
    ...mapGetters(["isAuthenticatedUserHasAccess"]),
    filterItems() {
      return this.subsetFilter;
    },
    discoverCats(){
      return this.filters.filter(c => c.name !== 'tempmaincat' && !c.name.includes("_del") && !this.discoverCategories.includes(c._id) && this.checkChildrenVisibility(c.children));
    }
  },
  watch: {
    apiReady(val){
      if(val){
        console.log("Api status updated")
        this.initDiscover();
      }
    },
    documents() {
      this.exportReady = false;
      this.docsLoading = false;
    },
    collections() {
      this.setCollections();
      this.exportReady = false;
      this.docsLoading = false;
    },
    selectedCollection() {
      this.exportReady = false;
      this.docsLoading = false;
    },
    filterItems(){
      this.loadAnimStart();
      this.saveLoadingStep("Applying.....");
      setTimeout(() => {
        this.loadAnimEnd();
      }, this.layout[this.tabIndex].length*800);
      this.filterDocs();
    },
    filterFocused(){
      this.loadAnimStart();
      this.saveLoadingStep("Applying.....");
      setTimeout(() => {
        this.loadAnimEnd();
      }, this.layout[this.tabIndex].length*800);
      this.filterDocs();
    },
    tabIndex(val){
      if(val !== 0){
        this.loadAnimStart();
        this.saveLoadingStep("Setting up.....");
        setTimeout(() => {
          this.loadAnimEnd();
        }, this.layout.length? this.layout[this.tabIndex].length*800: 800);
      }
    }
  },
  mounted() {
    this.setCollections();
    if(this.apiReady){
      this.initDiscover();
    }
    EventBus.$on("showOptions", async (elements) => {
      if(this.tabIndex === 1){
        if (this.activeElement !== elements && elements !== null) {
          if (this.activeElement === null || this.activeElement === "null") {
            //this.toggleOptionsPanel();
          }
          this.activeElement = elements;
          //console.log("Active element set to:", this.activeElement);
          await this.saveSubsetFilter(["#doc-universe svg", [{ _id: this.activeElement._id }]]);
          this.filterDocs();
        } else {
          //this.toggleOptionsPanel();
          this.activeElement = elements;
          //console.log("Active element cleared");
          await this.saveSubsetFilter([null]);
          this.filterDocs();
        }
        this.selectedElements = [];
        this.elementTypes = [];
        this.selectedElementsObjects = [];
      }
      else{
        if (!Array.isArray(elements)) {
          // TODO: Always send "elements" as an array?
          elements = [[elements]];
        }
        this.selectedElements = [];
        this.elementTypes = [];
        this.selectedElementsObjects = [];
        let eltypes = new Set();
        elements.forEach(element => {
          let objs = [];
          element.forEach(el => {
            objs.push(el);
            eltypes.add(el.type);
          });
          this.selectedElements.push({
            name: objs.map(x => x.name).join(" AND ")
          });
          this.selectedElementsObjects.push(objs);
        });

        this.elementTypes = [...eltypes].map(x => {
          return { name: x };
        });
      }
    });
    EventBus.$on("showDocumentsPanel", () => {
      if (!this.showDocumentsPanel) {
        this.toggleDocumentsPanel();
      }
    });
    EventBus.$on("removeAll", () => {
      this.removeAll();
    });
    EventBus.$on("toggleTopPanel", (view) => {
      if (
        (!this.showTopPanel && view === "new") ||
        (this.showTopPanel && view === null)
      ) {
        this.toggleTopPanel();
      }
    });
  },
  beforeDestroy() {
    //DataSettings.setSettings("focus.layout", this.layout);
    this.clearSelections();
  },
  methods: {
    ...mapActions([
      "saveFilter",
      "saveSubsetFilter",
      "saveFilterQuery",
      "saveDocPanelState",
      "loadCollectionDocs",
      "loadDocuments",
      "exportDocuments",
      "loadFilters",
      "filterFocus",
      "saveActiveVisual",
      "loadAnimStart",
      "loadAnimEnd",
      "saveLoadingStep",
      "loadActiveElement",
      "saveFilterElement",
    ]),
    async initDiscover(){
      await this.initYears();
      this.loadAnimStart();
      await this.migrateToNewLayout();
      
      this.settings = DataSettings.getSettings("focus");
      if(this.isAuthenticatedUserHasAccess && (this.settings && this.settings.layout.length === 0)){
        this.missingData = await this.preInitPresets();
        //this.layout = new Array(4).fill(new Array());
        this.settings.layout = new Array(4).fill(new Array());
        this.showMissingAlert = true;
        this.loadAnimEnd();
      }
      else{
        this.layout = new Array(4).fill(new Array());
        this.discoverReady = true;
        if (this.settings.layout[2].length > 0 || this.settings.layout[3].length > 0) {
          this.restorelayout();
        }
        if (this.filterFocused) {
          this.saveFilterQuery({ query: this.filterFocused.rawquery });
        }
      }
      /*
      if(!this.showMissingAlert){
        this.loadAnimStart();
        this.saveLoadingStep("Initializing.....");
        setTimeout(() => {
          this.loadAnimEnd();
        }, this.layout[this.tabIndex].length*800);
      }
      */

    },
    async migrateToNewLayout() {
      return new Promise((resolve) => {
        
        let settings = DataSettings.getSettings("focus.layout");

        if(settings && settings.length === 5){
          this.saveLoadingStep("Migrating layout.....");
          // settings is guaranteed to be either empty or an array of 5 elements.
          if (Array.isArray(settings) && settings.length === 5) {
            //console.log('Removing 4th element from settings:', settings[3]);
            settings.splice(3, 1);
          } else {
            //console.log('settings is empty or not length 5:', settings);
          }
          //console.log("Focus settings loaded:", settings);
          DataSettings.setSettings("focus.layout", settings);
          setTimeout(() => {
            this.loadAnimEnd();
            resolve();
          }, 2000);
        }
        else{
          resolve();
        }
        
      });
    },
    async continueInit(){
      this.showMissingAlert = false;
      this.layout = await this.initPresets();
      const newLayout = this.layout;
      if(this.isAuthenticatedUserHasAccess){
        DataSettings.setSettings("focus.layout", newLayout);
      }
      this.restorelayout();
      if (this.filterFocused) {
        this.saveFilterQuery({ query: this.filterFocused.rawquery });
      }

      this.loadAnimStart();
      this.saveLoadingStep("Initializing.....");
      setTimeout(() => {
        this.loadAnimEnd();
      }, this.layout[this.tabIndex].length*800);
    },
    async skipInit(){
      this.loadAnimStart();
      this.saveLoadingStep("Setting up empty layout.....");
      this.layout = new Array(4).fill(new Array());
      const newLayout = this.layout;
      if(this.isAuthenticatedUserHasAccess){
        DataSettings.setSettings("focus.layout", newLayout);
        //console.log("Saved empty layout for focus");
      }
      this.settings = DataSettings.getSettings("focus");
      setTimeout(() => {
        this.loadAnimEnd();
        this.saveLoadingStep("");
        this.showMissingAlert = false;
      }, 2000);
    },
    async preInitPresets(){
      let reqIndMeta = [
        { list: ["scopuscountry", "dwcc", "ctcou", "dwsct", "pmcty", "countrycode"], label: "Country" }, 
        { list: ["yr"], label: "Year" }, 
        { list:  ["organizations", "dwap", "aa", "pmaa"], label: "Organizations" },
        { list: ["authors","author", "inventor", "dwinv", "pma", "scfa" ], label: "Authors" }
      ];
      let missing = [];
      return new Promise((resolve) => {
        reqIndMeta.forEach(async(r, n) => {
          if(!(_.find(this.indices, i => { return r.list.includes(i.name) })) && !(_.find(this.metas, m => { return r.list.includes(m[0][0]) }))){
            if(!missing.includes(r.label)){
              missing.push(r.label);
            }
          }
          if(n+1 === reqIndMeta.length){
            const tempmaincat = await getFiltId(null, "tempmaincat");
            if(!tempmaincat){
              let filter = {
                name: "default_placeholder",
                type: "filter",
                query: [{"_id":"Fields_1"}]
              }
              this.saveFilter({
                filter: filter,
                category: { "name": "tempmaincat" },
                subcategory: { "name": "tempsubcat" }
              });
            }
            if(this.filters.length < 2){
              missing.push("User Filters");
            }
            else{
              //DataSettings.setSettings("focus.lastQuery", this.filters.filter(f => f.name !== "tempmaincat" ));
              if(this.isAuthenticatedUserHasAccess){
                DataSettings.setSettings("focus.lastQuery", []);
              }
            }
            resolve(missing);
          }
        });
      });
    },
    async initYears() {
      return new Promise((resolve, reject) => {
        if (this.documentsPerYear.length > 0) {
          const yearMin = parseInt(_.minBy(this.documentsPerYear, "year").year);
          const yearMax = parseInt(_.maxBy(this.documentsPerYear, "year").year);
          const yearRecent = yearMax - 2;
          this.years = [ yearMin, yearMax, yearRecent];
          resolve();
        }
        else{
          this.years = [1933,2023,2021];
          resolve();
        }
      })
    },
    async initPresets(){
      return new Promise((resolve) => {
        let db = localStorage.getItem("currentDbType");
        let normal_color = "size";
        let alt_color = "recency";
        let normal_metric = "size";
        let normal_metricX = "size";
        let normal_metricY = "size";
        let normal_metricX_plot = "recency";
        let normal_xsort = "normal";
        let normal_ysort = "normal";
        let alt_xsort = "size_des";
        let alt_ysort = "size_des";
        let dbIndex = 0;
        let ath = "";
        let org = "";
        let yr = "";
        let cn = "";
        let titles = [
          ["Applicants", "Geography", "Activity", "Categories", "Topics", "Inventors", "Topic Trends", "SubCategories", "Geography", "Category Trends", "Organizations"],
          ["Organizations", "Geography", "Activity", "Categories", "Mesh Terms", "Authors", "Topic Trends", "SubCategories", "Geography", "Category Trends", "Organizations"],
          ["Organizations", "Geography", "Activity", "Categories", "Topics", "Authors", "Topic Trends", "SubCategories", "Geography", "Category Trends", "Organizations"]
        ]
        if(db === "derwent"){
          dbIndex = 0;
          ath = "dwinv";
          org = "dwap";
          yr = "yr";
          cn = "dwcc";
        }
        else if(db === "scopus"){
          dbIndex = 2;
          ath = "scfa";
          org = "aa";
          yr = "yr";
          cn = "scopuscountry";
        }
        else if(db === "pubmed"){
          dbIndex = 1;
          ath = "pma";
          org = "pmaa";
          yr = "yr";
          cn = "country";
        }
        else if(db === "lens"){
          dbIndex = 1;
          ath = "authors";
          org = "organizations";
          yr = "yr";
          cn = "countrycode";
        }
        else if(db === "lens_patents"){
          ath = "applicants";
          org = "organizations";
          yr = "yr";
          cn = "country";
        }
        else if(db === "google_patent"){
          ath = "author";
          org = "company";
          yr = "yr";
          cn = "countrycode";
        }
        else if(db === "reddit"){
          ath = "author";
          org = "aa";
          yr = "yr";
          cn = "country";
        }
        else if(db === "cordis"){
          ath = "dwap";
          org = "companies";
          yr = "yr";
          cn = "country";
        }
        else if(db === "bbso"){
          ath = "employeereported";
          org = "observeedepartment";
          yr = "pubdate";
          cn = "geographiclocation";
        }
        else if(db === "kpmg"){
          ath = "keywords";
          org = "companies";
          yr = "pubdate";
          cn = "country";
        }
        else if(db === "pro_quest"){
          ath = "authors";
          org = "Companies";
          yr = "date";
          cn = "Country";
        }
        else {
          ath = "authors";
          org = "Companies";
          yr = "date";
          cn = "Country";
        }

        if(this.metas["Country"]){
          cn = this.metas["Country"][0][0];
        }
        else if(this.metas["Country Code"]){
          cn = this.metas["Country Code"][0][0];
        }
        else if(this.metas["Location"]){
          cn = this.metas["Location"][0][0];
        }
        else{
          cn = "scopuscountry";
        }
        
        let applicants = {"i":1,"x":0,"y":0,"w":2,"h":10,"type":"barchart","class":"none","query":{"config":{"query":{"xlayers":[{"layers":[[org,0]],"preselect":{"connect":false,"includeAll":false,"perGroup":false,"overlap":false}}],"zlayers":[null],"subset_filters":[],"startyear":this.years[0],"recentyear":this.years[2],"stopyear":this.years[1],"postprocessing":[],"ylayers":[]}}},"settings":{"view":"barchart","block":1,"controls":{"chartType":"barchart","showTitle":true,"showControls":true,"showQuery":true,"showColor":true,"showSortZ":false,"showSortX":true,"showSortY":false,"showNormalize":false,"showRecency":true,"showSize":true,"showMetric":true,"showMetricX":false,"showMetricY":false,"showTypeCats":false},"sortX":alt_xsort,"normalizeOverX":false,"metric":normal_metric,"metricX":normal_metricX,"metricXOptions":[{"value":"origsize","text":"Original Size"},{"value":"recency","text":"Recency"},{"value":"size","text":"Size"}],"typeCats":true,"sizeLimit":[1,180],"sizeRange":[1,180],"recencyLimit":[-3,3],"recencyRange":[-3,3],"color":alt_color,"colorOptions":[{"value":"origsize","text":"Original Size"},{"value":"recency","text":"Recency"},{"value":"size","text":"Size"}],"clustering":false},"name":titles[dbIndex][0],"description":null,"moved":false}
        let geography = {"i":2,"x":2,"y":0,"w":2,"h":10,"type":"worldmap","class":"none","query":{"config":{"query":{"xlayers":[{"layers":[[cn,0]],"preselect":{"connect":false,"includeAll":false,"perGroup":false,"overlap":false}}],"zlayers":[null],"subset_filters":[],"startyear":this.years[0],"recentyear":this.years[2],"stopyear":this.years[1],"postprocessing":[],"ylayers":[]}}},"settings":{"view":"worldmap","block":2,"controls":{"chartType":"worldmap","showTitle":true,"showControls":true,"showQuery":true,"showColor":true,"showSortZ":false,"showSortX":false,"showSortY":false,"showNormalize":false,"showRecency":true,"showSize":true,"showMetric":true,"showMetricX":false,"showMetricY":false,"showTypeCats":false},"normalizeOverX":false,"sortZOptions":[{"value":0,"text":"All"}],"sortZ":0,"metric":normal_metric,"metricX":normal_metricX,"metricXOptions":[{"value":"origsize","text":"Original Size"},{"value":"recency","text":"Recency"},{"value":"size","text":"Size"}],"metricY":normal_metricY,"metricYOptions":[{"value":"origsize","text":"Original Size"},{"value":"recency","text":"Recency"},{"value":"size","text":"Size"}],"typeCats":true,"sizeLimit":[1,1513],"sizeRange":[1,1513],"recencyLimit":[-3,3],"recencyRange":[-3,3],"color":normal_color,"colorOptions":[{"value":"origsize","text":"Original Size"},{"value":"recency","text":"Recency"},{"value":"size","text":"Size"}],"clustering":false},"name":titles[dbIndex][1],"description":null,"moved":false}
        let activity = {"i":3,"x":4,"y":0,"w":2,"h":10,"type":"linechart","class":"none","query":{"config":{"query":{"xlayers":[{"layers":[[yr,0]],"preselect":{"connect":false,"includeAll":false,"perGroup":false,"overlap":false}}],"zlayers":[null],"subset_filters":[],"startyear":this.years[0],"recentyear":this.years[2],"stopyear":this.years[1],"postprocessing":[],"ylayers":[]}}},"settings":{"view":"linechart","block":3,"controls":{"chartType":"linechart","showTitle":true,"showControls":true,"showQuery":true,"showColor":true,"showSortZ":false,"showSortX":false,"showSortY":false,"showNormalize":false,"showRecency":false,"showSize":true,"showMetric":true,"showMetricX":false,"showMetricY":false,"showTypeCats":false},"normalizeOverX":false,"sortZOptions":[{"value":0,"text":"All"}],"sortZ":0,"metric":normal_metric,"metricX":normal_metricX,"metricXOptions":[{"value":"origsize","text":"Original Size"},{"value":"recency","text":"Recency"},{"value":"size","text":"Size"}],"metricY":normal_metricY,"metricYOptions":[{"value":"origsize","text":"Original Size"},{"value":"recency","text":"Recency"},{"value":"size","text":"Size"}],"typeCats":true,"sizeLimit":[1,160],"sizeRange":[1,160],"recencyLimit":[-3,3],"recencyRange":[-3,3],"color":normal_color,"colorOptions":[{"value":"origsize","text":"Original Size"},{"value":"recency","text":"Recency"},{"value":"size","text":"Size"}],"clustering":false},"name":titles[dbIndex][2],"description":null,"moved":false}
        let categories = {"i":4,"x":0,"y":10,"w":2,"h":10,"type":"linechart","class":"none","query":{"config":{"query":{"xlayers":[{"layers":["filtergroup"],"preselect":{"connect":false,"includeAll":false,"perGroup":false,"overlap":false}}],"zlayers":[null],"subset_filters":[],"startyear":this.years[0],"recentyear":this.years[2],"stopyear":this.years[1],"postprocessing":[],"ylayers":[]}}},"settings":{"view":"linechart","block":4,"controls":{"chartType":"linechart","showTitle":true,"showControls":true,"showQuery":true,"showColor":true,"showSortZ":false,"showSortX":false,"showSortY":false,"showNormalize":false,"showRecency":false,"showSize":true,"showMetric":true,"showMetricX":false,"showMetricY":false,"showTypeCats":false},"normalizeOverX":false,"sortZOptions":[{"value":0,"text":"All"}],"sortZ":0,"metric":normal_metric,"metricX":normal_metricX,"metricXOptions":[{"value":"origsize","text":"Original Size"},{"value":"recency","text":"Recency"},{"value":"size","text":"Size"}],"metricY":normal_metricY,"metricYOptions":[{"value":"origsize","text":"Original Size"},{"value":"recency","text":"Recency"},{"value":"size","text":"Size"}],"typeCats":true,"sizeLimit":[1,160],"sizeRange":[1,160],"recencyLimit":[-3,3],"recencyRange":[-3,3],"color":normal_color,"colorOptions":[{"value":"origsize","text":"Original Size"},{"value":"recency","text":"Recency"},{"value":"size","text":"Size"}],"clustering":false},"name":titles[dbIndex][3],"description":null,"moved":false}
        let topics = null;
        if(dbIndex===0 || dbIndex===2) {
          topics = {"i":5,"x":2,"y":10,"w":2,"h":10,"type": "barchart","class":"none","query":{"config":{"query":{"xlayers":[{"layers":[["filt",0]],"preselect":{"connect":false,"includeAll":false,"perGroup":false,"overlap":false}}],"zlayers":[null],"subset_filters":[],"startyear":this.years[0],"recentyear":this.years[2],"stopyear":this.years[1],"postprocessing":[],"ylayers":[]}}},"settings":{"view":"barchart","block":5,"controls":{"chartType":"barchart","showTitle":true,"showControls":true,"showQuery":true,"showColor":true,"showSortZ":false,"showSortX":true,"showSortY":false,"showNormalize":false,"showRecency":true,"showSize":true,"showMetric":true,"showMetricX":false,"showMetricY":false,"showTypeCats":false},"sortX":alt_xsort,"normalizeOverX":false,"metric":normal_metric,"metricX":normal_metricX,"metricXOptions":[{"value":"origsize","text":"Original Size"},{"value":"recency","text":"Recency"},{"value":"size","text":"Size"}],"typeCats":true,"sizeLimit":[1,160],"sizeRange":[1,160],"recencyLimit":[-3,3],"recencyRange":[-3,3],"color":alt_color,"colorOptions":[{"value":"origsize","text":"Original Size"},{"value":"recency","text":"Recency"},{"value":"size","text":"Size"}],"clustering":false},"name":titles[dbIndex][4],"description":null,"moved":false}
        }
        else{
          topics = {"i":5,"x":0,"y":20,"w":2,"h":10,"type":"scatterplot","class":"none","query":{"config":{"query":{"xlayers":[{"layers":[["pmmh",0]],"preselect":{"connect":false,"includeAll":false,"perGroup":false,"overlap":false}}],"zlayers":[null],"subset_filters":[],"startyear":this.years[0],"recentyear":this.years[2],"stopyear":this.years[1],"postprocessing":[],"ylayers":[]}}},"settings":{"view":"scatterplot","block":7,"controls":{"chartType":"scatterplot","showTitle":true,"showControls":true,"showQuery":true,"showColor":false,"showSortZ":false,"showSortX":false,"showSortY":false,"showNormalize":false,"showRecency":true,"showSize":true,"showMetric":false,"showMetricX":true,"showMetricY":true,"showTypeCats":false}, "normalizeOverX":false,"metricX":normal_metricX_plot,"metricXOptions":[{"value":"origsize","text":"Original Size"},{"value":"recency","text":"Recency"},{"value":"size","text":"Size"}],"metricY":normal_metricY,"metricYOptions":[{"value":"origsize","text":"Original Size"},{"value":"recency","text":"Recency"},{"value":"size","text":"Size"}],"typeCats":true,"sizeLimit":[1,180],"sizeRange":[1,180],"recencyLimit":[-3,3],"recencyRange":[-3,3],"color":normal_color,"colorOptions":[{"value":"origsize","text":"Original Size"},{"value":"recency","text":"Recency"},{"value":"size","text":"Size"}],"clustering":false},"name":titles[dbIndex][4],"description":null,"moved":false}
        }
        let inventors = {"i":6,"x":4,"y":10,"w":2,"h":10,"type":"barchart","class":"none","query":{"config":{"query":{"xlayers":[{"layers":[[ath,0]],"preselect":{"connect":false,"includeAll":false,"perGroup":false,"overlap":false}}],"zlayers":[null],"subset_filters":[],"startyear":this.years[0],"recentyear":this.years[2],"stopyear":this.years[1],"postprocessing":[],"ylayers":[]}}},"settings":{"view":"barchart","block":6,"controls":{"chartType":"barchart","showTitle":true,"showControls":true,"showQuery":true,"showColor":true,"showSortZ":false,"showSortX":true,"showSortY":false,"showNormalize":false,"showRecency":true,"showSize":true,"showMetric":true,"showMetricX":false,"showMetricY":false,"showTypeCats":false},"sortX":alt_xsort,"normalizeOverX":false,"metric":normal_metric,"metricX":normal_metricX,"metricXOptions":[{"value":"origsize","text":"Original Size"},{"value":"recency","text":"Recency"},{"value":"size","text":"Size"}],"typeCats":true,"sizeLimit":[1,180],"sizeRange":[1,180],"recencyLimit":[-3,3],"recencyRange":[-3,3],"color":alt_color,"colorOptions":[{"value":"origsize","text":"Original Size"},{"value":"recency","text":"Recency"},{"value":"size","text":"Size"}],"clustering":false},"name":titles[dbIndex][5],"description":null,"moved":false}
        let topic_trends = {"i":7,"x":0,"y":20,"w":2,"h":10,"type":"scatterplot","class":"none","query":{"config":{"query":{"xlayers":[{"layers":[["filt",0]],"preselect":{"connect":false,"includeAll":false,"perGroup":false,"overlap":false}}],"zlayers":[null],"subset_filters":[],"startyear":this.years[0],"recentyear":this.years[2],"stopyear":this.years[1],"postprocessing":[],"ylayers":[]}}},"settings":{"view":"scatterplot","block":7,"controls":{"chartType":"scatterplot","showTitle":true,"showControls":true,"showQuery":true,"showColor":false,"showSortZ":false,"showSortX":false,"showSortY":false,"showNormalize":false,"showRecency":true,"showSize":true,"showMetric":false,"showMetricX":true,"showMetricY":true,"showTypeCats":false}, "normalizeOverX":false,"metricX":normal_metricX_plot,"metricXOptions":[{"value":"origsize","text":"Original Size"},{"value":"recency","text":"Recency"},{"value":"size","text":"Size"}],"metricY":normal_metricY,"metricYOptions":[{"value":"origsize","text":"Original Size"},{"value":"recency","text":"Recency"},{"value":"size","text":"Size"}],"typeCats":true,"sizeLimit":[1,180],"sizeRange":[1,180],"recencyLimit":[-3,3],"recencyRange":[-3,3],"color":normal_color,"colorOptions":[{"value":"origsize","text":"Original Size"},{"value":"recency","text":"Recency"},{"value":"size","text":"Size"}],"clustering":false},"name":titles[dbIndex][6],"description":null,"moved":false}
        let subcategories = {"i":8,"x":2,"y":20,"w":2,"h":10,"type":"scatterplot","class":"none","query":{"config":{"query":{"xlayers":[{"layers":["filtersubgroup"],"preselect":{"connect":false,"includeAll":false,"perGroup":false,"overlap":false}}],"zlayers":[null],"subset_filters":[],"startyear":this.years[0],"recentyear":this.years[2],"stopyear":this.years[1],"postprocessing":[],"ylayers":[]}}},"settings":{"view":"scatterplot","block":8,"controls":{"chartType":"scatterplot","showTitle":true,"showControls":true,"showQuery":true,"showColor":false,"showSortZ":false,"showSortX":false,"showSortY":false,"showNormalize":false,"showRecency":true,"showSize":true,"showMetric":false,"showMetricX":true,"showMetricY":true,"showTypeCats":false}, "normalizeOverX":false,"metricX":normal_metricX_plot,"metricXOptions":[{"value":"origsize","text":"Original Size"},{"value":"recency","text":"Recency"},{"value":"size","text":"Size"}],"metricY":normal_metricY,"metricYOptions":[{"value":"origsize","text":"Original Size"},{"value":"recency","text":"Recency"},{"value":"size","text":"Size"}],"typeCats":true,"sizeLimit":[1,180],"sizeRange":[1,180],"recencyLimit":[-3,3],"recencyRange":[-3,3],"color":normal_color,"colorOptions":[{"value":"origsize","text":"Original Size"},{"value":"recency","text":"Recency"},{"value":"size","text":"Size"}],"clustering":false},"name":titles[dbIndex][7],"description":null,"moved":false}
        
        
        //let geography_focus = {"i":1,"x":0,"y":0,"w":4,"h":20,"type":"worldmap","class":"none","query":{"config":{"query":{"xlayers":[{"layers":[[cn,0]],"preselect":{"connect":false,"includeAll":false,"perGroup":false,"overlap":false}}],"zlayers":[null],"subset_filters":[],"startyear":this.years[0],"recentyear":this.years[2],"stopyear":this.years[1],"postprocessing":[],"ylayers":[]}}},"settings":{"view":"worldmap","block":1,"controls":{"chartType":"worldmap","showTitle":true,"showControls":true,"showQuery":true,"showColor":true,"showSortZ":false,"showSortX":false,"showSortY":false,"showNormalize":false,"showRecency":true,"showSize":true,"showMetric":true,"showMetricX":false,"showMetricY":false,"showTypeCats":false},"normalizeOverX":false,"sortZOptions":[{"value":0,"text":"All"}],"sortZ":0,"metric":normal_metric,"metricX":normal_metricX,"metricXOptions":[{"value":"origsize","text":"Original Size"},{"value":"recency","text":"Recency"},{"value":"size","text":"Size"}],"metricY":normal_metricY,"metricYOptions":[{"value":"origsize","text":"Original Size"},{"value":"recency","text":"Recency"},{"value":"size","text":"Size"}],"typeCats":true,"sizeLimit":[1,1513],"sizeRange":[1,1513],"recencyLimit":[-3,3],"recencyRange":[-3,3],"color":normal_color,"colorOptions":[{"value":"origsize","text":"Original Size"},{"value":"recency","text":"Recency"},{"value":"size","text":"Size"}],"clustering":false},"name":titles[dbIndex][8],"description":null,"moved":false}
        //let category_trends = {"i":2,"x":4,"y":0,"w":2,"h":10,"type":"scatterplot","class":"none","query":{"config":{"query":{"xlayers":[{"layers":["filtergroup"],"preselect":{"connect":false,"includeAll":false,"perGroup":false,"overlap":false}}],"zlayers":[null],"subset_filters":[],"startyear":this.years[0],"recentyear":this.years[2],"stopyear":this.years[1],"postprocessing":[],"ylayers":[]}}},"settings":{"view":"scatterplot","block":2,"controls":{"chartType":"scatterplot","showTitle":true,"showControls":true,"showQuery":true,"showColor":false,"showSortZ":false,"showSortX":false,"showSortY":false,"showNormalize":false,"showRecency":true,"showSize":true,"showMetric":false,"showMetricX":true,"showMetricY":true,"showTypeCats":false}, "normalizeOverX":false,"metricX":normal_metricX_plot,"metricXOptions":[{"value":"origsize","text":"Original Size"},{"value":"recency","text":"Recency"},{"value":"size","text":"Size"}],"metricY":normal_metricY,"metricYOptions":[{"value":"origsize","text":"Original Size"},{"value":"recency","text":"Recency"},{"value":"size","text":"Size"}],"typeCats":true,"sizeLimit":[1,180],"sizeRange":[1,180],"recencyLimit":[-3,3],"recencyRange":[-3,3],"color":normal_color,"colorOptions":[{"value":"origsize","text":"Original Size"},{"value":"recency","text":"Recency"},{"value":"size","text":"Size"}],"clustering":false},"name":titles[dbIndex][9],"description":null,"moved":false}
        //let organizations_focus = {"i":3,"x":4,"y":10,"w":2,"h":10,"type":"barchart","class":"none","query":{"config":{"query":{"xlayers":[{"layers":[[org,0]],"preselect":{"connect":false,"includeAll":false,"perGroup":false,"overlap":false}}],"zlayers":[null],"subset_filters":[],"startyear":this.years[0],"recentyear":this.years[2],"stopyear":this.years[1],"postprocessing":[],"ylayers":[]}}},"settings":{"view":"barchart","block":3,"controls":{"chartType":"barchart","showTitle":true,"showControls":true,"showQuery":true,"showColor":true,"showSortZ":false,"showSortX":true,"showSortY":false,"showNormalize":false,"showRecency":true,"showSize":true,"showMetric":true,"showMetricX":false,"showMetricY":false,"showTypeCats":false},"sortX":alt_xsort,"normalizeOverX":false,"metric":normal_metric,"metricX":normal_metricX,"metricXOptions":[{"value":"origsize","text":"Original Size"},{"value":"recency","text":"Recency"},{"value":"size","text":"Size"}],"typeCats":true,"sizeLimit":[1,180],"sizeRange":[1,180],"recencyLimit":[-3,3],"recencyRange":[-3,3],"color":alt_color,"colorOptions":[{"value":"origsize","text":"Original Size"},{"value":"recency","text":"Recency"},{"value":"size","text":"Size"}],"clustering":false},"name":titles[dbIndex][10],"description":null,"moved":false}
        
        // focus set for derwent
        //let applicants_focus = {"i":3,"x":4,"y":10,"w":2,"h":10,"type":"barchart","class":"none","query":{"config":{"query":{"xlayers":[{"layers":[[org,0]],"preselect":{"connect":false,"includeAll":false,"perGroup":false,"overlap":false}}],"zlayers":[null],"subset_filters":[],"startyear":this.years[0],"recentyear":this.years[2],"stopyear":this.years[1],"postprocessing":[],"ylayers":[]}}},"settings":{"view":"barchart","block":3,"controls":{"chartType":"barchart","showTitle":true,"showControls":true,"showQuery":true,"showColor":true,"showSortZ":false,"showSortX":true,"showSortY":false,"showNormalize":false,"showRecency":true,"showSize":true,"showMetric":true,"showMetricX":false,"showMetricY":false,"showTypeCats":false},"sortX":alt_xsort,"normalizeOverX":false,"metric":normal_metric,"metricX":normal_metricX,"metricXOptions":[{"value":"origsize","text":"Original Size"},{"value":"recency","text":"Recency"},{"value":"size","text":"Size"}],"typeCats":true,"sizeLimit":[1,180],"sizeRange":[1,180],"recencyLimit":[-3,3],"recencyRange":[-3,3],"color":alt_color,"colorOptions":[{"value":"origsize","text":"Original Size"},{"value":"recency","text":"Recency"},{"value":"size","text":"Size"}],"clustering":false},"name":titles[dbIndex][10],"description":null,"moved":false}
        
        //let heatmap = {"i":1,"x":0,"y":0,"w":6,"h":20,"type":"heatmap","class":"none","query":{"config":{"query":{"xlayers":[{"layers":[this.discoverCats.length>0? this.discoverCats[0]._id:[ath,0]],"preselect":{"connect":false,"includeAll":false,"perGroup":false,"overlap":false,"top":100}}],"zlayers":[null],"subset_filters":[],"startyear":this.years[0],"recentyear":this.years[2],"stopyear":this.years[1],"postprocessing":["clustering"],"ylayers":[{"layers":[this.tree.selected[this.tabIndex]],"preselect":{"connect":false,"includeAll":false,"perGroup":false,"overlap":false,"top":100}}]}}},"settings":{"view":"heatmap","block":1,"controls":{"chartType":"heatmap","showTitle":true,"showControls":true,"showQuery":true,"showColor":true,"showSortZ":true,"showSortX":true,"showSortY":true,"showNormalize":false,"showRecency":true,"showSize":true,"showMetric":false,"showMetricX":true,"showMetricY":true,"showTypeCats":false},"sortX":alt_xsort,"sortY":alt_ysort,"normalizeOverX":false,"sortZOptions":[{"value":0,"text":"All"}],"sortZ":0,"metric":normal_metric,"metricX":normal_metricX,"metricXOptions":[{"value":"origsize","text":"Original Size"},{"value":"recency","text":"Recency"},{"value":"size","text":"Size"}],"metricY":normal_metricY,"metricYOptions":[{"value":"origsize","text":"Original Size"},{"value":"recency","text":"Recency"},{"value":"size","text":"Size"}],"typeCats":true,"sizeLimit":[1,180],"sizeRange":[1,180],"recencyLimit":[-3,3],"recencyRange":[-3,3],"color":alt_color,"colorOptions":[{"value":"origsize","text":"Original Size"},{"value":"recency","text":"Recency"},{"value":"size","text":"Size"}],"clustering":true},"name":"Correlations","description":null,"moved":false}

        let newLayout = [];

        newLayout.push([]);
        newLayout.push([]);
        newLayout.push([]);
        newLayout.push([]);
        //newLayout.push([]);
        if(!this.missingData.includes("Organizations")){
          if(["scopus", "lens", "pubmed"].includes(db)){
            newLayout[2].push(applicants);
            //newLayout[3].push(organizations_focus);
          }
          else if(db === "derwent"){
            newLayout[2].push(applicants);
            //newLayout[3].push(applicants_focus);
          }
        }
        if(!this.missingData.includes("Country")){
          if(["scopus", "lens", "pubmed"].includes(db)){
            newLayout[2].push(geography);
            //newLayout[3].push(geography_focus);
          }
          else if(db === "derwent"){
            newLayout[2].push(geography);
            //newLayout[3].push(geography_focus);
          }
        }
        if(!this.missingData.includes("Year")){
          if(["scopus", "lens", "pubmed"].includes(db)){
            newLayout[2].push(activity);
          }
          else if(db === "derwent"){
            newLayout[2].push(activity);
          }
        }
        if(!this.missingData.includes("Authors")){
          if(["scopus", "lens", "pubmed"].includes(db)){
            newLayout[2].push(inventors);
            //newLayout[3].push(heatmap);
          }
          else if(db === "derwent"){
            newLayout[2].push(inventors);
            //newLayout[3].push(heatmap);
          }
        }
        if(!this.missingData.includes("User Filters")){
          if(["scopus", "lens", "pubmed"].includes(db)){
            newLayout[2].push(categories);
            newLayout[2].push(topics);
            newLayout[2].push(topic_trends);
            newLayout[2].push(subcategories);
            //newLayout[3].push(category_trends);
          }
          else if(db === "derwent"){
            newLayout[2].push(categories);
            newLayout[2].push(topics);
            newLayout[2].push(topic_trends);
            newLayout[2].push(subcategories);
            //newLayout[3].push(category_trends);
          }
        }
        resolve(newLayout);
      })
    },
    downloadSvg(id) {
      let svg = this.$refs['anlview'+id][0].querySelector("svg");
      downloadSvg(svg);
    },
    downloadPng(id) {
      let svg = this.$refs['anlview'+id][0].querySelector("svg");
      downloadSvg2Png(svg);
    },
    /*
    downloadPpt() {
      let svg = this.$refs.anlview.querySelector("svg");
      downloadPpt(svg);
    },
    */
    downloadXls(id) {
      let crainData = this.$refs['anl'+id][0].visualization.control.crainData;
      downloadXls(crainData);
    },
    setCollections() {
      this.options = [{ value: null, text: "Select Collection" }];
      _.each(this.collections, (coll) => {
        this.options.push({
          value: coll._id,
          text: coll.name,
        });
      });
    },
    getCollectionDocs() {
      this.loadCollectionDocs(this.selectedCollection);
    },
    restorelayout() {
      this.settings = DataSettings.getSettings("focus");
      const layoutCopy = this.layout;
      let i = 0;
      while (i < 4) {
        const oldLayout = this.settings.layout[i];
        if(i == 0){
          if(oldLayout && oldLayout.length){
            this.form = oldLayout[0];
          }
        }
        let tabLayout = [];
        if(Array.isArray(oldLayout)){
          oldLayout.forEach((element) => {
            this.visQuery = element.query;
            tabLayout.push(element);
            let newLayout = layoutCopy;
            newLayout[i] = tabLayout;
            this.layout = newLayout;
          });
        }
      i++;
      }
    },
    newBlockForm(){
      this.showNewBlockForm = !this.showNewBlockForm;
    },
    closeTopPanel(){
      this.blockSelected = null;
      this.quickSettings = null;
      this.showNewBlockForm = false;
    },
    initNewBlock(preset){
      this.layoutSelected = preset[1];
      this.addNewBlock(preset[0], preset[2]);
    },
    addNewBlock(query, details) {
      const layoutCopy = JSON.parse(JSON.stringify(this.layout));
      let posX =
        layoutCopy[this.tabIndex].length > 2
          ? (layoutCopy[this.tabIndex].length - Math.trunc(layoutCopy[this.tabIndex].length / 3) * 3) * 2
          : layoutCopy[this.tabIndex].length * 2;
      let posY = Math.trunc(layoutCopy[this.tabIndex].length / 3) * 10;
      let tabLayout = layoutCopy[this.tabIndex];
      tabLayout.push({
        i: layoutCopy[this.tabIndex].length > 0 ? _.maxBy(layoutCopy[this.tabIndex], "i").i + 1 : 1,
        x: posX,
        y: posY,
        w: 2,
        h: 10,
        type: this.layoutSelected,
        class: "none",
        query: {
          config: { query: query },
        },
        settings: {
          view: this.layoutSelected,
          block: layoutCopy[this.tabIndex].length > 0 ? _.maxBy(layoutCopy[this.tabIndex], "i").i + 1 : 1,
          controls: settingControls[
            _.findIndex(settingControls, { chartType: this.layoutSelected })
          ],
          sortX: this.layoutSelected === "heatmap"? "clustering":"size_des",
          sortXOptions: [
            { value: "normal", text: "Normal" },
            { value: "size", text: "Size" },
            { value: "size_des", text: "Size descending" },
            { value: "recency", text: "Recency" },
            { value: "recency_des", text: "Recency descending" },
            {
              value: "clustering",
              text: "Clustering",
              disabled: !query.postprocessing.includes("clustering")
            },
            {
              value: "clustering_docs",
              text: "Clustering (Documents)",
              disabled: !query.postprocessing.includes("clustering")
            }
          ],
          sortY: this.layoutSelected === "heatmap"? "clustering":"size_des",
          sortYOptions: [
            { value: "normal", text: "Normal" },
            { value: "size", text: "Size" },
            { value: "size_des", text: "Size descending" },
            { value: "recency", text: "Recency" },
            { value: "recency_des", text: "Recency descending" },
            {
              value: "clustering",
              text: "Clustering",
              disabled: !query.postprocessing.includes("clustering")
            },
            {
              value: "clustering_docs",
              text: "Clustering (Documents)",
              disabled: !query.postprocessing.includes("clustering")
            }
          ],
          normalizeOverX: false,
          sortZOptions: this.sortZOptions,
          sortZ: 0,
          sizeLimit: null,
          sizeRange: null,
          recencyLimit: null,
          recencyRange: null,
          color: this.layoutSelected === "heatmap"? "size":"recency",
          colorOptions: defaultColorOptions,
          metric: "size",
          metricX: this.layoutSelected === "scatterplot"? "recency":"size",
          metricXOptions: defaultMetricOptions,
          metricY: "size",
          metricYOptions: defaultMetricOptions,
          typeCats: true,
          clustering: query.postprocessing.includes("clustering")
        },
        name: details.name,
        description: details.description
      });
      let newLayout = layoutCopy;
      newLayout[this.tabIndex] = tabLayout;
      this.layout = newLayout; 
      if(this.isAuthenticatedUserHasAccess){
        DataSettings.setSettings("focus.layout", newLayout);
      }
    },
    updateBlock(block) {
      let index = _.findIndex(this.layout[this.tabIndex], function (b) {
        return b.i === block;
      });
      let oldlayout = this.layout[this.tabIndex][index];
      oldlayout.type = oldlayout.settings.view = this.layoutSelected;
      this.layout[this.tabIndex].splice(index, 1, oldlayout);
      if(this.isAuthenticatedUserHasAccess){
        DataSettings.setSettings("focus.layout", this.layout);
      }
      //this.restorelayout();
    },
    removeBlock(item) {
      // if removing a block whilte it is maximized, restore the saved full layout and remove the block from there instead of the minimized layout which only contains the maximized block
      if (this.blockMaximized && item.i === this.blockMaximized.i && Array.isArray(this.blockMaximized.landmark)) {
        const landmarkCopy = JSON.parse(JSON.stringify(this.blockMaximized.landmark));
        const idx = _.findIndex(landmarkCopy, (o) => o.i === item.i);

        if (idx !== -1) {
          landmarkCopy.splice(idx, 1);
        }

        this.layout[this.tabIndex] = landmarkCopy;
        this.blockMaximized.i = null;
        this.blockMaximized.item = null;
        this.blockMaximized.index = null;
        this.blockMaximized.landmark = null;
        if (this.isAuthenticatedUserHasAccess){
            DataSettings.setSettings("focus.layout", this.layout);
          }
        return;

      }

      //const layoutCopy = JSON.parse(JSON.stringify(this.layout));
      const itemToRemove = _.findIndex(this.layout[this.tabIndex], function(o) { return o.i == item.i; });
      if (itemToRemove !== -1) {
        const tabLayout = this.layout[this.tabIndex].slice();
        tabLayout.splice(itemToRemove, 1);
        this.layout[this.tabIndex] = tabLayout; 
      }
      if (this.isAuthenticatedUserHasAccess){
        DataSettings.setSettings("focus.layout", this.layout);
      } 
    },
    removeAll() {
      this.layout[this.tabIndex] = [];
      
      this.blockMaximized = null;
    },
    selectBlock(i) {
      if (this.blockSelected === i) {
        this.blockSelected = null;
        this.quickSettings = null;
      } else {
        //let index = _.findIndex(this.layout[this.tabIndex], function (b) {
        //  return b.i === i;
        //});
        this.blockSelected = i;
        this.quickSettings = null;
        //this.quickSettings = this.layout[this.tabIndex][index].settings;
      }
    },
    setDefault(defaults) {
      let index = _.findIndex(this.layout[this.tabIndex], function (b) {
        return b.i === defaults.block;
      });
      //console.log(index, defaults.block);
      this.layout[this.tabIndex][index].settings = defaults;
    },
    updateSettings(settings) {
      let index = _.findIndex(this.layout[this.tabIndex], function (b) {
        return b.i === settings.block;
      });
      this.layout[this.tabIndex][index].settings = settings;
    },
    changeLayout(layout) {
      this.layoutSelected = layout;
      this.updateBlock(this.blockSelected);
    },
    layoutReadyEvent() {
      this.layoutReady = true;
    },
    maximizeBlock(block) {
      const layoutCopy = JSON.parse(JSON.stringify(this.layout[this.tabIndex]));
      if (this.blockMaximized.i === block.i) {
        this.layout[this.tabIndex] = this.blockMaximized.landmark;
        this.layout[this.tabIndex][this.blockMaximized.index].x = this.blockMaximized.item.x;
        this.layout[this.tabIndex][this.blockMaximized.index].y = this.blockMaximized.item.y;
        this.layout[this.tabIndex][this.blockMaximized.index].w = this.blockMaximized.item.w;
        this.layout[this.tabIndex][this.blockMaximized.index].h = this.blockMaximized.item.h;
        this.blockMaximized.i = null;
        this.blockMaximized.item = null;
        this.blockMaximized.index = null;
        this.blockMaximized.landmark = null;
        setTimeout(() => this.resizedEvent({vis: block.i, act: "min"}), 100);
      } else {
        let index = _.findIndex(layoutCopy, (item) => item.i === block.i);
        //document.getElementById("focus-page").scrollTop = 0;
        this.blockMaximized.i = block.i;
        this.blockMaximized.item = block;
        this.blockMaximized.index = index;
        this.blockMaximized.landmark = layoutCopy;
        this.layout[this.tabIndex] = [];
        this.layout[this.tabIndex].push(layoutCopy[index]);
        this.layout[this.tabIndex][0].x = 0;
        this.layout[this.tabIndex][0].y = 0;
        this.layout[this.tabIndex][0].w = 6;
        this.layout[this.tabIndex][0].h = 20;
        //this.layout[this.tabIndex][0].moved = true;
        setTimeout(() => this.resizedEvent({vis: block.i, act: "max" }), 100);
      }
    },
    resizedEvent(item) {
      EventBus.$emit("resizeSingleChart", item);
      /*if (this.$refs) {
        console.log(this.$refs.datamap);
        this.$refs.datamap[0].resize();
      }*/
    },
    clearDocFilter() {
      this.loadActiveElement({ element: null });
      this.saveFilterElement({ element: null });
      this.saveFilterQuery({ query: [] });
      this.selectedElements = [];
      this.selectedElementsObjects = [];
      this.elementTypes = [];
    },
    clearSelections() {
      this.saveFilterQuery({ query: [] });
      this.saveSubsetFilter([null]);
      this.saveActiveVisual([null]);
    },
    showDocumentModal(key) {
      if (!this.showDocumentsPanel) {
        this.toggleDocumentsPanel();
      }
      this.$refs.docPanel.openDocumentDetail(key);
    },
    toggleLeftSidebar() {
      this.showLeftSidebar = !this.showLeftSidebar;
    },
    toggleTopPanel() {
      this.showTopPanel = !this.showTopPanel;
    },
    toggleDocumentsPanel() {
      this.showDocumentsPanel = !this.showDocumentsPanel;
    },
    resizeDocumentsPanel() {
      this.panelExpanded === "default"
        ? this.saveDocPanelState("expanded")
        : this.saveDocPanelState("default");
    },
    exportDocs() {
      this.docsLoading = !this.docsLoading;
      if (this.docsLoading) {
        /*this.loadDocuments({
          which: "global",
          page: 1,
          pageSize: this.documents.length,
          sortOn: "pubdate",
          sortAsc: false,
        }).then(() => {*/
        /*let docLen = this.documents.length;
        if(docLen > 5){
          let itr = 0;
          while(itr < docLen/5) {
            this.exportDocuments({ docs: _.map(this.documents.slice[itr*5, (itr*5)+6], "webkey"), model: this.apiId }).then(() => {
              this.formatExportDocuments(true);
              //this.exportReady = true;
              itr++;
            });
          }
        }
        else{*/
          this.exportDocuments({ docs: _.map(this.documents, "webkey"), model: this.apiId }).then(() => {
            this.formatExportDocuments();
            this.exportReady = true;
          });
        //}
        //});
      } else {
        this.exportReady = false;
      }
    },
    getMetaValues(values) {
      const v = values.map((meta) => meta[0]);
      let metaVals = [];
      v.forEach((val) => {
        metaVals.push(val.name);
      });
      return metaVals.join(" | ");
    },
    async formatExportDocuments(batch = false) {
      if(!batch){
        this.exportData = [];
      }
      this.documentsExport.forEach((d) => {
        let text = {};
        let tempObj = {};
        d.text.forEach((t) => {
          tempObj[t.name] = t.text;
          text = { ...text, ...tempObj };
        });
        Object.entries(d.meta).forEach((entry) => {
          const [key, value] = entry;
          tempObj[key.split("_")[1]] = this.getMetaValues(value);
          text = { ...text, ...tempObj };
        });
        this.exportData.push(text);
      });
    },
    resetCollection() {
      this.selectedCollection = null;
    },
    async checkFilter(e, inx) {
      //console.log(e, this.tabIndex, inx);
      const tree = inx !== null ? this.tree.selected[this.tabIndex][inx]:this.tree.selected[this.tabIndex];
      // Create a shallow copy to avoid direct mutation and ensure Vue reactivity
      let workingTree = [...tree];
      //console.log(tree, e._id);
      if(typeof e === 'object' && e !== null && e.children.length > 0){
        const scope = tree.includes(e._id);
        if(scope){
          let index = _.indexOf(workingTree, e._id);
          workingTree.splice(index, 1);
          console.log("inside", workingTree);
          inx !== null ? this.$set(this.tree.selected[this.tabIndex], inx, workingTree) : this.$set(this.tree.selected, this.tabIndex, workingTree);
          e.children.forEach(async (s) => {
            index = _.indexOf(workingTree, s._id);
            if(index > -1){
              workingTree.splice(index, 1);
              inx !== null ? this.$set(this.tree.selected[this.tabIndex], inx, workingTree) : this.$set(this.tree.selected, this.tabIndex, workingTree);
            }
            if(s.children && s.children.length > 0){
              s.children.forEach(async (f) => {
                index = _.indexOf(workingTree, f._id);
                if(index > -1){
                  console.log("inside f");
                  workingTree.splice(index, 1);
                  inx !== null ? this.$set(this.tree.selected[this.tabIndex], inx, workingTree) : this.$set(this.tree.selected, this.tabIndex, workingTree);
                }
              })
            }
          });
        }
        else{
          if(!e.visibility || e.visibility.discover){
            workingTree.push(e._id);
            inx !== null ? this.$set(this.tree.selected[this.tabIndex], inx, workingTree) : this.$set(this.tree.selected, this.tabIndex, workingTree);
          }
          e.children.forEach(async (s) => {
            if(!workingTree.includes(s._id) && (!s.visibility || s.visibility.discover)){
              workingTree.push(s._id);
              inx !== null ? this.$set(this.tree.selected[this.tabIndex], inx, workingTree) : this.$set(this.tree.selected, this.tabIndex, workingTree);
            }
            if(s.children && s.children.length > 0){
              s.children.forEach(async (f) => {
                if(!workingTree.includes(f._id) && (!f.visibility || f.visibility.discover)){
                  workingTree.push(f._id);
                  inx !== null ? this.$set(this.tree.selected[this.tabIndex], inx, workingTree) : this.$set(this.tree.selected, this.tabIndex, workingTree);
                }
              })
            }
          });
        }
      }
      else{
        if(tree.includes(e)){
          let index = _.indexOf(workingTree, e);
          workingTree.splice(index, 1);
          inx !== null ? this.$set(this.tree.selected[this.tabIndex], inx, workingTree) : this.$set(this.tree.selected, this.tabIndex, workingTree);
        }
        else{
          if(!e.visibility || e.visibility.discover){
            workingTree.push(e);
            inx !== null ? this.$set(this.tree.selected[this.tabIndex], inx, workingTree) : this.$set(this.tree.selected, this.tabIndex, workingTree);
          }
        }
      }
    },
    checkChildrenVisibility(list){
      //let visible = list.filter(l => { return !l.visibility || l.visibility.discover });
      let visible = list.filter(l => { return !this.discoverCategories.includes(l._id) && !l.name.includes('_del')});
      return visible.length > 0
    },
    async getFilter(el){
      return new Promise((resolve, reject) => {
        this.filters.forEach(cat => {
          if(cat._id === el._id){
            resolve(cat);
          }
          else if(cat.children && cat.children.length > 0){
            cat.children.forEach(sub => {
              if(sub._id === el._id){
                resolve(sub);
              }
              else if(sub.children && sub.children.length > 0){
                sub.children.forEach(filt => {
                  if(filt._id === el._id){
                    resolve(filt);
                  }
                });
              }
            });
          }
        });
      });
    },
    async prepareFilterQuery(){
      return new Promise((resolve, reject) => {
        if(this.tree.selected[this.tabIndex].length === 0){
          resolve([]);
        }
        let mapped = this.tree.selected[this.tabIndex].map(f => {
          return { _id: f }
        });
        let result = [];
        mapped.forEach(async (el, n) => {
          this.getFilter(el).then(filt => {
            if(filt.type === 'Userfilter' && !filt._id.includes('invalid_filter_')){
              result.push(el);
              result.push("OR");
            }
            if(mapped.length === n+1){
              result.pop();
              resolve(result);
            }
          });
        });
      });
    },
    async applyFilters(){
      let filterToApply = null;
      if(this.tree.selected[this.tabIndex].length > 0){
        filterToApply = {
          name: "Filter Query",
          rawquery: [await this.prepareFilterQuery()],
        }
        if(filterToApply.rawquery[0].length>0){
          this.filterFocus({ filter: filterToApply, type: "focus" }).then(() => {
            console.log("Filter Applied");
          });
        }
        else{
          this.$toasted.show("Nothing to Apply! Please check filters to Apply.", {
            type: "error",
          });
        }
      }
      else{
        this.$toasted.show("Nothing to Apply! Please check filters to Apply.", {
          type: "error",
        });
      }
      
    },
    resetFilters(){
      //this.tree.selected[this.tabIndex] = [];
      this.filterFocus({ filter: null, type: "focus" }).then(() => {
        console.log("Filter Removed");
      });
    },
    applyCat(id){
      this.appliedCatId = id;
      if(id){
        let filts = [];
        this.filters.forEach(cat => {
          if(cat._id === id){
            if(this.checkChildrenVisibility(cat.children)){
            cat.children.forEach((sub, i) => {
              if(!this.discoverCategories.includes(sub._id) && !sub.name.includes('_del')){
                sub.children.forEach((filt, n) => {
                  if(!this.discoverCategories.includes(filt._id) && !filt.name.includes('_del') && !(filt._id.includes('invalid_filter_'))){
                    filts.push({_id: filt._id});
                    filts.push("OR");
                  }
                  if(cat.children.length === i+1 && sub.children.length === n+1){
                    filts.pop();
                    if(filts.length>0){
                      this.filterFocus({ 
                        filter: {
                          name: "Filter Query",
                          rawquery: [filts],
                        }, 
                        type: "focus" }).then(() => {
                          console.log("Filter Applied");
                      });
                    }
                    else{
                      this.$toasted.show("Nothing to Apply! Selected Category is empty", {
                        type: "error",
                      });  
                    }
                  }
                });
              }
            });
            }
            else{
              this.$toasted.show("Nothing to Apply! Selected Category is empty", {
                type: "error",
              }); 
            }
          }
        });
      }
      else{
        this.filterFocus({ filter: null, type: "focus" }).then(() => {
          console.log("Filter Removed");
        });
      }
    },
    async correlationsAxis(val, axis){
      // Todo: the top categories on correlations taking deleted filters as well.
      /*
      let cRelation = JSON.parse(JSON.stringify(this.layout[this.tabIndex][0].query.config.query));
      let oldX = cRelation.xlayers[0].layers;
      let oldY = cRelation.ylayers[0].layers;
      //let newX, newY = [];
      if(axis){
        if(oldX.includes(val)){
          if(oldX.length > 1){
            let index = _.indexOf(oldX, val);
            oldX.splice(index, 1);
          }
          else{
            this.$toasted.show("You cant unselect all the categories. Atleast one Category is required!", {
              type: "error",
            });
          }
        }
        else{
          oldX.push(val);
        }
        this.correlationsCatIds = oldX;

        //setting up Y axis
        const yList = this.tree.selected[this.tabIndex][1].filter(f => f.includes("filt_") && !this.discoverCategories.includes(f));
        if(yList.length === 0){
          this.$toasted.show("You cant unselect all the filters. Atleast one filter is required!", {
            type: "error",
          });
        }
        else{
          oldY = yList;
        }
      }
      */
      if(this.tree.selected[this.tabIndex][0].filter(f => f.includes("filt_")).length || this.tree.selected[this.tabIndex][1].filter(f => f.includes("filt_")).length){
        let layoutCopy = JSON.parse(JSON.stringify(this.layout));
        let newLayout = layoutCopy;
        newLayout[this.tabIndex][0].query.config.query.xlayers[0].layers = this.tree.selected[this.tabIndex][0].filter(f => f.includes("filt_"));
        newLayout[this.tabIndex][0].query.config.query.ylayers[0].layers = this.tree.selected[this.tabIndex][1].filter(f => f.includes("filt_"));
        this.layout = newLayout; 
        if(this.isAuthenticatedUserHasAccess){
          DataSettings.setSettings("focus.layout", newLayout);
        }
      }
      else{
        this.$toasted.show("You cant unselect all the filters or categories. Atleast one from both is required!", {
          type: "error",
        });
      }    
    },
    showTrending(){
      this.recencySelection = "trending";
      let oldLayout = this.layout[this.tabIndex];
      oldLayout.forEach(block => {
        block.settings.recencyLimit = [1,3];
        //block.settings.recencyRange = [-1,1];
        //block.settings.color = "recency";
      });
      const newLayout = oldLayout;
      //this.layout[this.tabIndex] = [];
      this.layout[this.tabIndex] = newLayout;
    },
    showEstablished(){
      this.recencySelection = "established";
      let oldLayout = this.layout[this.tabIndex];
      oldLayout.forEach(block => {
        block.settings.recencyLimit = [-1,1];
        //block.settings.color = "size";
        //block.settings.recencyRange = [-3,3];
        //block.settings.color = "size";
      });
      const newLayout = oldLayout;
      //this.layout[this.tabIndex] = [];
      this.layout[this.tabIndex] = newLayout;
    },
    showDeclining(){
      this.recencySelection = "declining";
      let oldLayout = this.layout[this.tabIndex];
      oldLayout.forEach(block => {
        block.settings.recencyLimit = [-3,-1];
        //block.settings.recencyRange = [-3,3];
        //block.settings.color = "size";
      });
      const newLayout = oldLayout;
      //this.layout[this.tabIndex] = [];
      this.layout[this.tabIndex] = newLayout;
    },
    showDefault(){
      this.recencySelection = "default";
      const oldLayout = this.layout[this.tabIndex];
      oldLayout.forEach(block => {
        block.settings.recencyLimit = [-3,3];
        //block.settings.recencyRange = [-3,3];
        //block.settings.color = "size";
      });
      this.layout[this.tabIndex] = [];
      this.layout[this.tabIndex] = oldLayout;
    },
    toggleCr(){
      this.layout[this.tabIndex][0].type === "heatmap"? this.layout[this.tabIndex][0].type = "clustermap":this.layout[this.tabIndex][0].type = "heatmap";
    },
    resetTab(){
      this.appliedCatId = null;
      this.correlationsCatIds = [];
      this.tree.selected = [[],[],[],[[],[]]];
      if(this.filterFocused){
        this.filterFocus({ filter: null, type: "focus" }).then(() => {
          this.saveSubsetFilter([null]);
          this.saveActiveVisual([null]);
          this.initDiscover();
        });
      }
      else{
        this.saveSubsetFilter([null]);
        this.saveActiveVisual([null]);
        this.initDiscover();
      }
    },
    async resetDiscover(){
      this.layout = [];
      this.settings = null;
      this.tabIndex = 1;
      if(this.isAuthenticatedUserHasAccess){
        DataSettings.setSettings("focus.layout", []);
      }
      //await this.initYears();
      setTimeout(() => {
        this.initDiscover();
      }, 1000)
    },
    filterDocs() {
      //let { filt } = parseSelectedElements(this.filterItems);
      //let selection = prepFilter(this.filterItems, this.filterFocused);
      this.saveFilterQuery({ query: this.filterItems });
    },
    toggleCat(id, inx){
      console.log(inx);
      if(this.treeOpened[inx].indexOf(id) > -1){
        let index = this.treeOpened[inx].indexOf(id);
        this.treeOpened[inx].splice(index, 1);
      }
      else{
        this.treeOpened[inx].push(id);
      }
    },
    tabChanged(){
      if(this.tabIndex !== 1){
        this.showOptionsPanel =  false;
      }
      this.selectedElements = [];
      this.elementTypes = [];
      this.selectedElementsObjects = [];
      this.appliedCatId = null;
      this.correlationsCatIds = [];
      this.tree.selected = [[],[],[],[[],[]]];
      this.treeOpened = [[],[]];
      if(this.subsetFilter.length > 0){
        this.saveSubsetFilter([null]);
        this.saveActiveVisual([null]);
      }
      if(this.filterFocused){
        this.filterFocus({ filter: null, type: "focus" }).then(() => {
          console.log("Filter Removed");
        });
      }
      if(this.tabIndex === 3 && this.layout[this.tabIndex].length > 0){
        //this.correlationsCatIds = this.layout[this.tabIndex].query.config.query.xlayers[0].layers;
        this.tree.selected[this.tabIndex][0] = this.layout[this.tabIndex][0].query.config.query.xlayers[0].layers;
        this.tree.selected[this.tabIndex][1] = this.layout[this.tabIndex][0].query.config.query.ylayers[0].layers;
      }
      if(this.tabIndex === 3 && this.layout[this.tabIndex].length === 0){
        let outerCats = [];
        this.discoverCats.forEach((c, n) => {
          //this.checkFilter(c, null);
          outerCats.push(c._id);
          if(this.discoverCats.length === n+1){
            //this.correlationsCatIds.push(this.discoverCats[0]._id);
            let heatmap = {"i":1,"x":0,"y":0,"w":6,"h":20,"type":"heatmap","class":"none","query":{"config":{"query":{"xlayers":[{"layers":this.cleanTree(this.tree.selected[this.tabIndex][0]),"preselect":{"connect":false,"includeAll":false,"perGroup":false}}],"zlayers":[null],"subset_filters":[],"startyear":this.years[0],"recentyear":this.years[2],"stopyear":this.years[1],"postprocessing":["clustering"],"ylayers":[{"layers":this.cleanTree(this.tree.selected[this.tabIndex][0]),"preselect":{"connect":false,"includeAll":false,"perGroup":false}}]}}},"settings":{"view":"heatmap","block":1,"controls":{"chartType":"heatmap","showTitle":true,"showControls":true,"showQuery":true,"showColor":true,"showSortZ":true,"showSortX":true,"showSortY":true,"showNormalize":false,"showRecency":true,"showSize":true,"showMetric":false,"showMetricX":true,"showMetricY":true,"showTypeCats":false},"sortX":"clustering","sortY":"clustering","normalizeOverX":false,"sortZOptions":[{"value":0,"text":"All"}],"sortZ":0,"metric":"size","metricX":"size","metricXOptions":[{"value":"origsize","text":"Original Size"},{"value":"recency","text":"Recency"},{"value":"size","text":"Size"}],"metricY":"size","metricYOptions":[{"value":"origsize","text":"Original Size"},{"value":"recency","text":"Recency"},{"value":"size","text":"Size"}],"typeCats":true,"sizeLimit":[1,180],"sizeRange":[1,180],"recencyLimit":[-3,3],"recencyRange":[-3,3],"color":"size","colorOptions":[{"value":"origsize","text":"Original Size"},{"value":"recency","text":"Recency"},{"value":"size","text":"Size"}],"clustering":true},"name":"Correlations","description":null,"moved":false}
            const layoutCopy = JSON.parse(JSON.stringify(this.layout));
            let newLayout = layoutCopy;
            newLayout[this.tabIndex].push(heatmap);
            //Add all cats as default
            newLayout[this.tabIndex][0].query.config.query.xlayers[0].layers = outerCats;
            newLayout[this.tabIndex][0].query.config.query.ylayers[0].layers = outerCats;
            this.layout = newLayout;
            this.tree.selected[this.tabIndex][0] = outerCats;
            this.tree.selected[this.tabIndex][1] = outerCats;
            if(this.isAuthenticatedUserHasAccess){
              DataSettings.setSettings("focus.layout", newLayout);
            }
          }
        })
      }
    },
    checkTree(id, index) {
      const tree = this.tree.selected[3];
      return tree[index].length > 0 && tree[index].includes(id);
    },
    cleanTree(tree){
      return tree.filter(f => { return !f.includes('invalid_filter_') })
      //return tree;
    },
    saveAbout(){
      this.layout[this.tabIndex] = [this.form];
      if(this.isAuthenticatedUserHasAccess){
        DataSettings.setSettings("focus.layout", this.layout);
      }
      this.$toasted.show("Content Saved!", {
        type: "success",
      });
    },
    toggleAboutPreview(){
      this.showAboutPreview = !this.showAboutPreview;
    },
    toggleOptionsPanel() {
      this.showOptionsPanel = !this.showOptionsPanel;
    },
    toggleFilterBar() {
      this.filterBarVisible = !this.filterBarVisible;
    },
  },
};
</script>
